/**
 * Der Reiter „Dokumente" des Bestell-Drawers - Portierung von `drawerDocsTab` (`index.html:9198-9214`),
 * gefolgt vom Rest der Checkliste/Upload-Kette: `renderDocChecklist` (`:12639-12667`, ausgelagert nach
 * `Checkliste.tsx`), `uploadDocFor`/`startDocumentUpload`/`addDocs` (`:12673-13000`), `renderDocs`
 * (`:12910-12924`), `renderOrderExcel` (`:12745-12787`, nur die leere Ausgangslage - siehe unten) und
 * `renderPiImport(o, true)` (`:10417-10437`, ebenfalls nur die leere/„Prüfung ausstehend"-Lage).
 *
 * Aufbau, in der Reihenfolge des Originals (`drawerDocsTab`):
 *
 *   sec('docs-order-excel', 'Bestellexcel')
 *   sec('docs-pi-import',   'PI-Auswertung')
 *   sec('docs-check',       'Dokumente – Checkliste')     Checkliste.tsx
 *   sec('docs-sonst',       'Sonstige Dokumente')
 *   sec('docs-overwrite-log', `Überschreibungen · N`)     NUR wenn `documentOverwriteLog` Eintraege hat
 *
 * Zusaetzlich (KEIN Gegenstueck im Bestand-Rumpf, siehe `Verteilung.tsx`): die Betragsverteilung einer
 * Sendungsgruppe, sichtbar nur bei mehr als einem Gruppenmitglied - fuer `best-03` (Baseline) nie.
 *
 * ================= RECHTE: fast alles sperrt der Rechte-Durchgang des Drawers =================
 *
 * Wie `BestellungTab` (Task 17) und `TasksTab` (Task 18): weder `drawerDocsTab` noch
 * `renderDocChecklist`/`uploadDocFor`/`renderDocs` enthalten eine einzige `hasPermission`-Pruefung
 * oder ein `data-permission` (nachgezaehlt: `data-permission` kommt im ganzen `index.html` nur EINMAL
 * vor, `:8632` im Zahlungs-Reiter). Gesperrtwerden ist fast ueberall Sache des aeusseren
 * Rechte-Durchgangs (`Drawer.tsx`), der `.drawer-body input, select, textarea, button` erfasst - und
 * damit auch die per-Zeile-Datei-Felder dieses Reiters (siehe `Checkliste.tsx`).
 *
 * AUSNAHME seit „Dokument entfernen“ (Spec 2026-10-16): der Muelleimer ist sichtbar, wird aber hier selbst
 * `disabled` gesetzt, wenn dem Nutzer das Recht fuer den Dokumenttyp fehlt (`darfEntfernen`). Grund: der
 * Rechte-Durchgang des Drawers laeuft nicht erneut, wenn die Belegliste spaet geladen wird und der
 * Muelleimer erst danach entsteht - ohne die eigene Sperre bliebe er bis zum naechsten Zeichnen des
 * Drawers bedienbar (im Bestand gab es dieses Problem nicht, `index.html` zeichnet die Zeilen synchron).
 * `data-permission` am Knopf bleibt trotzdem noetig, damit der Durchgang ihn fuer die Zahlungsrolle
 * (kein `manage_documents`) nicht sperrt.
 *
 * Ticket F: der Muelleimer am Artwork einer Position (`Positionsdokumente.tsx`, Prop `rechte`) ist anders: er wird NICHT
 * gesperrt, sondern erscheint nur mit `manage_documents` - dem Recht des Hochladens dort. Die Liste „Entfernte Dokumente“ unten
 * nennt bei Artwork und Produktionsbildern zusaetzlich die Position (`lib/positionsAnzeige.ts#entferntesDokumentPosition`).
 *
 * ================= Zwei bewusst NICHT gebaute Flaechen (Naht-Grenze, siehe Task-Bericht) =========
 *
 * 1. Die volle PI-Editiertabelle (`renderPiImport(o, false)`, mit Positionen, Drag-Sortierung,
 *    Feldkorrektur) gehoert NICHT zu `drawerDocsTab` - sie steht ausschliesslich im separaten Dialog
 *    `#piReviewOverlay` (`openPiReview`, statisches Markup ausserhalb des Drawers). Dieser Reiter
 *    zeigt deshalb nur die KOMPAKTE Lage (`renderPiImport(o, true)`), die `drawerDocsTab` tatsaechlich
 *    einbettet - "Jetzt prüfen" oeffnet im Bestand besagten Dialog; hier ist der Knopf vorhanden,
 *    aber ohne Wirkung (kein Dialog in diesem Task gebaut).
 * 2. Der volle Bestellexcel-Vorschautisch (`order-excel-table`, `renderOrderExcel`s Positionen-
 *    Vorschau) ist auf die leere Ausgangslage und einen schlichten „Datei liegt vor"-Zustand
 *    reduziert - die Baseline (`best-03`) zeigt ausschliesslich die leere Lage, die hier
 *    Knoten-fuer-Knoten nachgebildet ist.
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Abschnitt } from "../Abschnitt.tsx";
import { Checkliste } from "./Checkliste.tsx";
import { Dokumentzeile } from "./Dokumentzeile.tsx";
import { GeerbtHinweis } from "./GeerbtHinweis.tsx";
import { UeberschreibDialog } from "./UeberschreibDialog.tsx";
import { ReichweiteDialog } from "./ReichweiteDialog.tsx";
import { DuplikatDialog } from "./DuplikatDialog.tsx";
import { Positionsdokumente, type ProduktionsbildOeffnen } from "./Positionsdokumente.tsx";
import type { MassenuploadAuftrag } from "./Massenupload/auftrag.ts";
import { MassenuploadAblage } from "./Massenupload/MassenuploadAblage.tsx";
import { Verteilung } from "./Verteilung.tsx";
import { DsvBelege } from "../DsvBelege.tsx";
import { belegKategorieEinstellung } from "../../../lib/belegKategorien.ts";
import { ladeParser } from "../../../lib/parsers/naht.ts";
import { dateiZuGrossText, istZuGross, uploadFehlerText } from "../../../lib/upload.ts";
import { UploadFehlerAnzeige } from "../../Upload/UploadFehlerAnzeige.tsx";
import { UploadAnsage, UploadFortschritt } from "../../Upload/UploadFortschritt.tsx";
import {
  uploadStelle,
  useFremdesUploadEnde,
  useUploadStelle,
  useUploadStellen,
  type UploadLauf,
  type UploadStellen,
} from "../../App/useUploadStellen.ts";
import { EntfernenDialog } from "./EntfernenDialog.tsx";
import { DOCUMENT_OCR_TYPES, DOC_TYPES } from "../../../config/documents.ts";
import { visibleDocTypes } from "../../../lib/status.ts";
import { ehemaligerDokumenttypLabel } from "../../../lib/zahlungsbelege.ts";
import { fmtDT, NUM } from "../../../lib/format.ts";
import { entfernenRechte, herkunftVon, kannEntfernen, reichweiteAnzahlVon } from "../../../lib/dokumentEntfernen.ts";
import { hatRecht, NUR_LESEN_TITEL } from "../drawerRechte.ts";
import { Ladefehler } from "../../Ladefehler/Ladefehler.tsx";
import { fehlertext } from "../../../lib/fehlertext.ts";
import { mitBestellbezug } from "../../../lib/bestellkennung.ts";
import { ApiFehler, KonfliktFehler } from "../../../api/basis.ts";
import { istAbgeleiteteKennung } from "../../../api/jtlBestellungen.ts";
import {
  checklisteEintragAendern,
  checklisteLaden,
  dokumenteLaden,
  dokumentEntfernen,
  dokumentPerHashLaden,
  dokumentVerknuepfen,
  entfernteDokumenteLaden,
} from "../../../api/dokumente.ts";
import type {
  DokumentAnsicht,
  DokumentPerHashTreffer,
  EntfernReichweite,
  EntferntesDokument,
} from "../../../api/dokumente.ts";
import { POSITIONSDOKUMENTE_LAEDT, type PositionsDokumenteZustand } from "../../../lib/positionsDokumenteZustand.ts";
import { kennungVon } from "../../../lib/bestellkennung.ts";
import type { ChecklistenEintrag, DsvBeleg, Order, OrderDocument, OrderItem, SendungsTreffer } from "../../../lib/types.ts";
import { entferntesDokumentPosition } from "../../../lib/positionsAnzeige.ts";
import type { Reichweite } from "../../../lib/dokumentReichweite.ts";
import { useDateiDownloadMitStapel } from "../../../api/useDateiDownload.ts";
import { meldeFehler, meldeNachladeFehler } from "../../../state/fehlerStapel.ts";
import { useMontiertRef } from "../../../state/useMontiertRef.ts";

/**
 * `icon(name, className)` (`index.html:4016`) samt der Nachbearbeitung durch `hydrateIcons`
 * (`:4021`) - die gemeinsame Registratur zeichnet das fertige `<svg class="lucide lucide-…">`.
 *
 * Bis `feat/09-spa-ikonen` stand hier eine LOKALE Funktion, die nur den Platzhalter
 * `<i data-lucide="…">` erzeugte. Den hydriert in der SPA niemand (die UMD-Fassung laeuft allein
 * im Baseline-Rundlauf des Playwright-Harness) - es blieb eine leere Flaeche. Siehe `Ikone.tsx`.
 */
import { Ikone } from "../../Ikone.tsx";

/** `index.html:5047`, woertlich: der Eintrag aus `visibleDocTypes(o)` mit passender `id`. */
function docTypeForOrder(o: Order, typeId: unknown) {
  return visibleDocTypes(o).find((t) => t.id === typeId);
}

/**
 * So nennt der Reiter die beiden Dokumenttypen, die nicht in `DOC_TYPES` stehen: als Titel ihres Abschnitts
 * (`Abschnitt titel=…` unten, dort dieselben Konstanten). Alle uebrigen tragen ihr `label` aus der Checkliste.
 */
const BESTELLEXCEL_BEZEICHNUNG = "Bestellexcel";
const SONSTIGE_BEZEICHNUNG = "Sonstige Dokumente";

/**
 * Wo ein Ziehen von Dateien abgefangen wird: der Rumpf des Drawers (`Drawer.tsx`, `.drawer-body`), in dem dieser Reiter steht,
 * und das Overlay des offenen Massenupload-Dialogs (`.mu-overlay`). Der Dialog hängt als Geschwister des Drawers am Wurzelelement
 * der App, nicht im Rumpf; ein Drop darauf bliebe sonst bei niemandem, der Browser öffnete die Datei und verließe den Hub samt Lauf.
 */
const RUMPF_SELEKTOR = ".drawer-body, .mu-overlay";

/**
 * Dateien, die im Reiter neben ein Ziel fallen, soll der Browser nicht öffnen: er verließe damit den Hub (die Datei ersetzt die
 * Seite). Das Ziehen von Dateien (`dataTransfer.types` enthält `"Files"`) über dem Rumpf wird deshalb abgebrochen
 * (`preventDefault`, `dropEffect "none"`), wenn kein inneres Ziel es schon angenommen oder abgewiesen hat
 * (`defaultPrevented`, etwa die Ablage des Massenuploads). Gezogener Text und alles außerhalb des Rumpfs bleiben dem Browser.
 *
 * Als Hörer am `document` im Bubble-Schritt, nicht am Rumpf: React behandelt seine Ereignisse am Wurzelelement der Anwendung, und das
 * liegt ÜBER dem Rumpf. Ein Hörer am Rumpf liefe vor den `onDrop`/`onDragOver` der inneren Ziele und sähe ihr `preventDefault` nicht.
 * Der Reiter selbst hat kein Wurzelelement (er gibt ein Fragment zurück; ein Hüllknoten bräche die Strukturparität gegen die Baseline).
 */
function dateiZiehenNebenDemZielAbfangen(ereignis: Event): void {
  if (ereignis.defaultPrevented) return;
  const daten = (ereignis as Event & { dataTransfer?: DataTransfer | null }).dataTransfer;
  if (!daten || !Array.from(daten.types).includes("Files")) return;
  if (!(ereignis.target instanceof Element) || ereignis.target.closest(RUMPF_SELEKTOR) === null) return;
  ereignis.preventDefault();
  daten.dropEffect = "none";
}

/**
 * Die Bezeichnung eines Dokumenttyps, wie sie der Reiter anzeigt - fuer den stehenden Hinweis, wenn ein Upload
 * scheitert, nachdem der Reiter weg ist (Fix-Runde 1 zu Task 5). Rueckfall auf die Typ-ID wie bei den Dialogen.
 */
function dokumentTypBezeichnung(o: Order, typeId: string): string {
  if (typeId === "bestellexcel") return BESTELLEXCEL_BEZEICHNUNG;
  if (typeId === "sonstige") return SONSTIGE_BEZEICHNUNG;
  return docTypeForOrder(o, typeId)?.label ?? typeId;
}

/**
 * Ob ein `doc_type` an DIESER Bestellung tatsaechlich eine Dokumentzeile zeichnet (Spezifikation
 * § 3.9/§ 5.4, Fixrunde 1 zu Aufgabe 7, Befund K-2). NICHT dasselbe wie "steht der Typ in `pdfs`":
 * zwei echte Dokumenttypen (`backend/src/documents/docTypes.ts` - `ankunftsavis`, `seefracht_buchungsbestaetigung`)
 * koennen an einer Bestellung liegen, ohne dass dieser Reiter je eine Zeile fuer sie zeichnet: `visibleDocTypes`
 * (`lib/status.ts:334`) fuehrt einen FESTEN Satz Checklistentypen, und der `sonstige`-Filter unten
 * greift nur bei einem `type`, der LEER ist oder woertlich `"sonstige"` heisst - keiner der zwei
 * Typen erfuellt eines von beidem. Ein Tieflink auf einen solchen Typ fand bisher trotzdem "etwas in
 * `pdfs`" und blieb STUMM: Reiter offen, nichts hervorgehoben, keine Meldung (konkreter Fall damals: eine
 * Split-Payment-Bestellung mit hochgeladenem `zahlungsbeleg_anzahlung`; seither steht dafuer `anzahlungsbeleg` in der
 * Checkliste, Asana 1220057765558651).
 *
 * EXPORTIERT, weil `App.tsx`s § 5.4-Pruefung GENAU DIESELBE Frage braucht wie die drei Einbaustellen
 * in diesem Reiter (Bestellexcel-Karte, Checkliste, "Sonstige Dokumente").
 *
 * BERICHTIGT (Fixrunde 2, Befund 4): hier stand, `App.tsx` benutze "dieselbe Funktion, die auch die
 * drei Einbaustellen des Reiters selbst kennt" - das ist ungenau. Die drei Einbaustellen RUFEN diese
 * Funktion NICHT auf: `bestellexcelEintrag` (unten) sucht selbst per `.find()`, die Checkliste
 * urteilt je Zeile per `zeilenUrteil` (`checklistenZeile.ts`), der `sonstige`-Filter (unten) prueft
 * selbst. Diese Funktion ist eine DRITTE, unabhaengige Formulierung derselben Regel - nichts im Code
 * haelt sie zusammen. Seit Spec 2026-11-03-drawer-checkliste-gleichlauf zeigt die Checkliste MEHR
 * als diese Funktion: eine Zeile, die der Server einblendet, oder einen Zahlungsbeleg nur an der
 * Ist-Zahlung (nicht in `o.pdfs`). Ein Tieflink auf so einen Typ meldet `App.tsx` weiter als nicht
 * angezeigt; das bleibt bewusst so (Spec, Entscheidung 3); ein Umbau, der die drei Stellen wirklich ueber
 * diese Funktion liefe, war innerhalb dieser Fixrunde nicht ohne groessere Verschiebung moeglich
 * (die Checkliste/der `sonstige`-Filter brauchen die tatsaechlichen Eintraege, nicht nur ein
 * Boolean - ein Aufruf hier waere zirkulaer). Was der Export WIRKLICH schliesst: `App.tsx` bekommt
 * dieselbe Regel als eine einzige, benannte Stelle statt einer VIERTEN, eigenen Fassung.
 */
export function istGezeichneterDokumentTyp(o: Order, docType: string): boolean {
  const pdfs = (o.pdfs as OrderDocument[] | undefined) || [];
  if (!pdfs.some((d) => String(d.type ?? "") === docType)) return false;
  return (
    docType === "bestellexcel" ||
    docType === "sonstige" ||
    visibleDocTypes(o).some((t) => t.id === docType)
  );
}

/**
 * Die Positionsdokumenttypen stehen nicht in `DOC_TYPES` (das ist die Checkliste der Bestelldokumente) - ohne diese Zuordnung
 * stuende in „Entfernte Dokumente“ der rohe Schluessel „artwork“ (Ticket F). Ein Eintrag ist EIN Bild: Einzahl.
 */
const POSITIONSDOKUMENT_LABEL: Readonly<Record<string, string>> = {
  artwork: "Artwork",
  produktionsbilder: "Produktionsbild",
};

/**
 * Die Bezeichnung des Dokumenttyps eines Eintrags in „Entfernte Dokumente“. Reihenfolge: Positionsdokument, Checklistentyp, ehemaliger
 * Typ (`zahlungsbeleg` & Co. stehen in `document_link_removals` absichtlich weiter, `lib/zahlungsbelege.ts`), erst zuletzt der
 * rohe Schluessel.
 */
function entferntTypLabel(docType: string): string {
  return (
    POSITIONSDOKUMENT_LABEL[docType] ??
    DOC_TYPES.find((t) => t.id === docType)?.label ??
    ehemaligerDokumenttypLabel(docType) ??
    docType
  );
}

/** „allen Bestellungen der Sendung DSV-9001“ - die Reichweite eines Entfernens in „Entfernte Dokumente“. */
function entferntReichweiteText(e: EntferntesDokument): string {
  if (e.herkunft === "gruppe") return "allen Bestellungen der Bestellgruppe";
  const nr = e.shipmentNo ? ` ${e.shipmentNo}` : "";
  if (e.herkunft === "sammelsendung") return `allen Bestellungen der Sammelsendung${nr}`;
  return `allen Bestellungen der Sendung${nr}`;
}

/** `index.html:12669-12671`, woertlich - hier auch von diesem Modul gebraucht (Sonstige Dokumente). */
function documentTypeAllowsMultiple(type: string): boolean {
  return type === "sonstige";
}

/**
 * SHA-256 einer Datei, hex-kodiert - client-seitig VOR dem eigentlichen Upload berechnet (Auftrag
 * "Duplikaterkennung beim Dokumenten-Upload per SHA-256", 07.09.2026, "Vereinbartes Design"), damit
 * eine erkannte Dublette NIE uebertragen und NIE auf die Volume geschrieben werden muss -
 * `documents/storage.ts`/der bestehende Streaming-Upload bleiben dadurch unangetastet.
 *
 * Liefert `null` statt zu werfen, wenn `crypto.subtle` fehlt (z. B. unsicherer Kontext, oder in
 * einer Testumgebung ohne Web-Crypto-Polyfill) ODER die Berechnung aus einem anderen Grund scheitert
 * - der Aufrufer (`dateiGewaehlt`) behandelt das als "kein Treffer ermittelbar" und faehrt mit dem
 * normalen Upload fort (fail open): eine defekte Duplikatpruefung darf einen an sich funktionierenden
 * Upload niemals verhindern.
 */
async function sha256Hex(datei: File): Promise<string | null> {
  try {
    const subtle = globalThis.crypto?.subtle;
    if (!subtle) return null;
    const puffer = await datei.arrayBuffer();
    const digest = await subtle.digest("SHA-256", puffer);
    return Array.from(new Uint8Array(digest))
      .map((byte) => byte.toString(16).padStart(2, "0"))
      .join("");
  } catch (fehler) { // still: fail open - ohne Hash entfaellt nur die Duplikatpruefung, der Upload laeuft weiter (siehe oben)
    console.error(`SHA-256 von "${datei.name}" konnte nicht berechnet werden:`, fehler);
    return null;
  }
}

/**
 * Eigene, spezifische Meldung fuer einen gescheiterten `PATCH .../documents/:docType`
 * (`checklisteStatusUmschalten` unten) - AUSDRUECKLICH NICHT der generische Bestellungs-Toast
 * ("Die Bestellung konnte nicht geändert werden.", `App.tsx#drawerAendern`), den dieser Reiter fuer
 * diesen Schreibweg gar nicht mehr durchlaeuft (Auftrag "Dokumente manuell abhaken", Root Cause).
 */
export const DOKUMENTSTATUS_FEHLGESCHLAGEN_MELDUNG = "Der Dokumentstatus konnte nicht geändert werden.";

/**
 * Nachbesserung (Pruefung b5748fc4, Befund G1): bis hierher blieb ein Nachlade-Fehlschlag NACH einem
 * erfolgreich durchgelaufenen PATCH stumm - `checklisteNeuLaden` faengt seinen eigenen Fehler intern ab
 * (`console.error`, siehe dort) und liefert danach eine AUFGELOESTE Zusage, nie eine abgelehnte; das
 * `.catch` an `checklisteStatusUmschalten` griff fuer diesen Fall folglich nie. Die Zeile zeigte darum
 * weiter "offen", ohne jede Meldung - ein zweiter Klick sandte danach eine veraltete `rowVersion` und
 * scheiterte mit 409. Wortlaut angelehnt an `App.tsx#NACHLADEN_FEHLGESCHLAGEN_MELDUNG` (dortselbst
 * Kopfkommentar).
 *
 * Der Satz sagt "die Anzeige", nicht "die Checkliste" (Zweig-Review Board-Dokumentstand): seit die Bestellung im
 * Store mit nachgeladen wird (`onBestellungNachladen`), kann auch NUR sie scheitern - dann ist die Checkliste
 * aktuell, und veraltet sind Board-Gruppe, Banner und Archivierung (sie lesen `dokumentStand` der Bestellung).
 * Scheitert dagegen die Checkliste, ist sie selbst veraltet. Dieselbe Konstante dient allen vier Wegen, die
 * nach einem gelungenen Schreiben nachladen: Dokumentstatus (`checklisteStatusUmschalten`), Verknuepfen
 * (`duplikatBestaetigen`), Entfernen (`entfernenAusfuehren`) und Belegzuordnung (`belegZugeordnet`). Bei allen
 * vieren ist die Aenderung beim Nachladen schon gespeichert und kann je nach Zweig eine andere Ansicht
 * (Checkliste, Dokumentliste, Bestellung im Store) veraltet sein; "die Anzeige" stimmt dort jeweils.
 */
const CHECKLISTE_NACHLADEN_FEHLGESCHLAGEN_MELDUNG =
  "Gespeichert - die Anzeige ist aber nicht mehr aktuell. Bitte neu laden.";

/**
 * Fehleranzeige Paket 1, Task 8, Fix-Runde 1 (Ruling R-T8a): der Satz fuer den UPLOAD-Weg. Dort laeuft das
 * Nachladen der Checkliste nach JEDEM Versuch, auch nach einem gescheiterten Upload (den `hochladenAusfuehren`
 * selbst als Zeile meldet, siehe dort). Scheitert das Nachladen, ist es ein eigener, zweiter Fehler: "Gespeichert ..."
 * waere eine Behauptung, die ein gescheiterter Upload widerlegt. Der Satz sagt nur, was feststeht; die Ursache haengt
 * `meldeNachladeFehler` dahinter - ausser sie raet zum Wiederholen (R-Z2): ob der Upload gelungen ist, sagt allein
 * seine Zeile, und ein "erneut versuchen" liesse ihn womoeglich doppelt laufen.
 */
const CHECKLISTE_NACHLADEN_NACH_UPLOAD_MELDUNG =
  "Die Checkliste konnte nicht neu geladen werden und ist womöglich nicht aktuell. Bitte neu laden.";

/**
 * Auftrag "Duplikaterkennung beim Dokumenten-Upload per SHA-256" (07.09.2026): Wortlaut fuer einen
 * gescheiterten `POST .../documents/:docType/link` (`duplikatBestaetigen` unten) - eigene, spezifische
 * Meldung aus demselben Grund wie `DOKUMENTSTATUS_FEHLGESCHLAGEN_MELDUNG` (kein generischer
 * Bestellungs-Toast, dieser Reiter laeuft fuer diesen Schreibweg nicht ueber `App.tsx#drawerAendern`).
 */
export const DUPLIKAT_VERKNUEPFUNG_FEHLGESCHLAGEN_MELDUNG = "Die Datei konnte nicht verknüpft werden.";

/**
 * Fix "Excel Download Template funktioniert nicht" (Asana 1218211471530480, 07.09.2026): Wortlaut fuer
 * einen gescheiterten Download ueber `herunterladen()` unten - siehe dortiger Kommentar fuer die
 * Root-Cause (rohes `<a href>` kann den Authorization-Header nicht mitschicken, 401).
 */
export const DOKUMENT_DOWNLOAD_FEHLGESCHLAGEN_MELDUNG = "Die Datei konnte nicht heruntergeladen werden.";

/**
 * Bestelldokument entfernen (Spec 2026-10-16): Wortlaut fuer ein gescheitertes
 * `DELETE .../document-links/:linkId`. Eigene Meldung statt des Bestellungs-Toasts - dieser Weg laeuft
 * nicht mehr ueber `onAendern`/`App.tsx#drawerAendern`. Ein 422 zeigt stattdessen den Text des Servers
 * (z. B. „Dokumente der Sammelsendung gelten für alle Bestellungen …“), ein 409 einen der drei Konfliktsaetze unten:
 */
export const DOKUMENT_ENTFERNEN_FEHLGESCHLAGEN_MELDUNG = "Das Dokument konnte nicht entfernt werden.";
/**
 * R4b (Fehleranzeige Paket 2, Task 12; Spec "Nachladen nach einem Schreiben"): nach einem 409 ist NICHTS entfernt. Erst wird
 * nachgeladen (`entfernenAusfuehren`), DANN steht GENAU EIN Eintrag da - nie zuerst einer, der etwas ueber den Stand sagt, und
 * danach ein zweiter, der das Gegenteil meldet. Der Konfliktsatz allein ist der dritte Ausgang (R-T10c: das Nachladen endete
 * ohne meldbaren Text, Abbruch oder 401): er sagt nichts ueber den Stand. Bewusst nicht "nicht mehr aktuell": das ist der
 * Wortlaut eines R4-Satzes, hier wurde aber nichts gespeichert.
 */
export const DOKUMENT_ENTFERNEN_KONFLIKT_MELDUNG = "Das Dokument wurde inzwischen geändert.";
export const DOKUMENT_ENTFERNEN_KONFLIKT_GELADEN_MELDUNG =
  `${DOKUMENT_ENTFERNEN_KONFLIKT_MELDUNG} Der aktuelle Stand ist geladen – bitte prüfen.`;
export const DOKUMENT_ENTFERNEN_KONFLIKT_NICHT_GELADEN_MELDUNG =
  `${DOKUMENT_ENTFERNEN_KONFLIKT_MELDUNG} Der aktuelle Stand konnte nicht geladen werden – bitte neu laden.`;
/** Scheitert nach dem Entfernen nur das Laden von „Entfernte Dokumente“ (die Checkliste ist dann aktuell). */
export const ENTFERNTE_DOKUMENTE_LADEN_FEHLGESCHLAGEN_MELDUNG =
  "Das Dokument ist entfernt, die Liste „Entfernte Dokumente“ konnte aber nicht aktualisiert werden. Bitte neu laden.";

/**
 * Die drei Teile, die der Reiter beim Oeffnen laedt (Fehleranzeige Paket 2, Task 12, Regel L1): Checkliste, Dokumentliste und
 * „Entfernte Dokumente“. Scheitert eines, steht oben im Reiter EINE Ladefehler-Flaeche, die alle gescheiterten Teile nennt
 * (Spec, Entscheidung E5). Die Reihenfolge hier ist die Reihenfolge im Satz und die der Ursache: hinter dem Satz steht die
 * Ursache des ersten gescheiterten Teils.
 */
type LadeTeil = "checkliste" | "dokumente" | "entfernte";
const LADE_TEILE: readonly LadeTeil[] = ["checkliste", "dokumente", "entfernte"];
const LADE_TEIL_NAME: Readonly<Record<LadeTeil, string>> = {
  checkliste: "die Checkliste",
  dokumente: "die Dokumentliste",
  entfernte: "die Liste „Entfernte Dokumente“",
};

/** „Die Checkliste und die Dokumentliste konnten nicht geladen werden.“ - Einzahl bei einem Teil, sonst Mehrzahl. */
function ladefehlerSatz(teile: readonly LadeTeil[]): string {
  const namen = teile.map((teil) => LADE_TEIL_NAME[teil]);
  const aufzaehlung = namen.length > 1 ? `${namen.slice(0, -1).join(", ")} und ${namen[namen.length - 1]}` : (namen[0] ?? "");
  const satz = `${aufzaehlung} ${namen.length > 1 ? "konnten" : "konnte"} nicht geladen werden.`;
  return satz.charAt(0).toUpperCase() + satz.slice(1);
}

/**
 * Welche Teile der Bestellung `orderId` beim Laden gescheitert sind, je Teil mit dem Fehler. Eingewickelt (`{ ursache }`), weil
 * auch `undefined` ein geworfener Wert sein kann. `orderId` aus demselben Grund wie bei Checkliste und Dokumenten: der Stand
 * einer anderen Bestellung gehoert nicht unter diese.
 */
interface LadefehlerStand {
  orderId: string;
  teile: Partial<Record<LadeTeil, { ursache: unknown }>>;
}

interface AusstehenderUpload {
  typeId: string;
  datei: File;
  bestehende: OrderDocument[];
  /**
   * Fix "Ueberschreiben scheitert immer mit 409" (07.09.2026): die `rowVersion` der bereits
   * vorhandenen `document_links`-Zeile (aus der Checkliste, siehe `rowVersionFuer` unten) - geht bis
   * zu `dokumentHochladen` durch (`ueberschreibenBestaetigen` -> `hochladenAusfuehren` ->
   * `onHochladen`). Ohne sie wirft `legeVersionAn` (`backend/src/documents/repository.ts`) fuer JEDE
   * bereits vorhandene Zeile IMMER einen `VersionConflictError` (409) - der Dialog konnte vor diesem
   * Fix strukturell nie erfolgreich sein.
   */
  rowVersion: number | null;
}

/**
 * Ein SHA-256-Treffer, der auf die Bestaetigung des Nutzers wartet (Auftrag "Duplikaterkennung beim
 * Dokumenten-Upload per SHA-256", 07.09.2026) - `typeId`/`datei` wie bei `AusstehenderUpload`, hier
 * zusaetzlich der Server-Treffer (`GET /api/documents/by-hash/:sha256`) fuer den Dialogtext und den
 * `documentId`, den `duplikatBestaetigen` an `dokumentVerknuepfen` weiterreicht.
 */
interface AusstehenderDuplikat {
  typeId: string;
  datei: File;
  treffer: DokumentPerHashTreffer;
}

/**
 * Sammelsendung (Task 4b): ein Schwung Dateien, der auf die Reichweiten-Wahl wartet
 * (`ReichweiteDialog.tsx`). `dateien` statt eines einzelnen `datei`-Felds (Fixrunde 1, Befund 1,
 * KRITISCH): "Sonstige Dokumente" erlaubt `multiple` - EIN gemeinsamer Wartezustand fuer den
 * GANZEN Schwung verhindert, dass ein zweiter Aufruf denselben State ueberschreibt und alle
 * Dateien ausser der letzten stumm verschwinden laesst (siehe `dateienGewaehlt` unten).
 *
 * KEIN `bestehende`-Feld mehr (Fixrunde 1, Befund 3): dieser Wartezustand entsteht jetzt nur noch
 * fuer eine echte ERSTABLAGE, siehe `dateienGewaehlt`.
 */
interface WartetAufReichweite {
  typeId: string;
  dateien: File[];
}

/**
 * Die stehenden Fehlerzeilen, mit Bestell-ID-Marke wie `checkliste`/`dokumente`: die Zeilen erscheinen nur, solange
 * der Reiter DIESE Bestellung zeigt (Fix-Runde 1) - sonst stuenden die Fehler von Bestellung A unter B.
 */
interface UploadFehlerListe {
  orderId: string;
  meldungen: string[];
}

/**
 * Was beim START eines Uploads festgehalten wird, nicht erst beim Scheitern gelesen: bis ein Upload von 100 MB
 * scheitert, zeigt dieselbe Instanz womoeglich eine andere Bestellung (oder es gibt sie nicht mehr), und die
 * Props waeren die der falschen. Der stehende Hinweis muss sagen, ZU WELCHER Bestellung und welchem Typ er gehoert.
 */
interface UploadStart {
  bestellungId: string;
  bestellNummer: string;
  typBezeichnung: string;
}

/** Platz der Datei in einer Auswahl aus mehreren - nur dann zaehlt die Zeile "Datei n von m". */
interface AuswahlPosition {
  nummer: number;
  gesamt: number;
}

export interface DokumenteTabProps {
  bestellung: Order;
  bestellungen: readonly Order[];
  /**
   * Bis zum Entfernen von Dokumenten ungenutzt (wie `BestellungTabProps.rechte`/`TasksTabProps.rechte`).
   * Seither dafuer gelesen, den Muelleimer ohne Recht fuer den Dokumenttyp zu sperren
   * (`lib/dokumentEntfernen.ts#entfernenRechte`, sichtbar aber `disabled` - `rechte/matrix.test.tsx`).
   * Grund fuer die eigene Sperre: der Rechtedurchgang des Drawers laeuft nach dem spaeten Laden der
   * Belegliste nicht erneut (siehe Kopfkommentar „RECHTE“). Alles Uebrige sperrt weiterhin allein er.
   */
  rechte: ReadonlySet<string>;
  onAendern: (id: string, aenderung: Record<string, unknown>) => void;
  /**
   * Der Naht-Punkt zum (in diesem Baum nicht vorhandenen) Upload-Endpunkt aus Phase 3b - siehe
   * Task-Bericht, Abschnitt "Was du sonst wissen musst". `notiz` ist nur bei einer Ueberschreibung
   * gesetzt (`confirmDocumentOverwrite`, `index.html:12723`).
   *
   * Rueckgabe seit der Nachbesserung (Punkt 4, 07.09.2026) `void | Promise<void>` statt nur `void`:
   * `hochladenAusfuehren` (unten) wartet per `await` auf den tatsaechlichen Netzwerkabschluss, um
   * die Multi-Upload-Schleife ("Sonstige Dokumente") serialisieren zu koennen - das setzt voraus,
   * dass `App.tsx#drawerDokumentHochladen` seine eigene Zusage zurueckgibt (siehe dortiger Kommentar).
   * Bestehende Aufrufer/Testattrappen, die weiterhin `void` liefern, bleiben gueltig (`await undefined`
   * laeuft sofort durch).
   *
   * `rowVersion` (Fix "Ueberschreiben scheitert immer mit 409", 07.09.2026): nur bei einer
   * Ueberschreibung gesetzt (wie `notiz`), aus `AusstehenderUpload.rowVersion` durchgereicht - siehe
   * `rowVersionFuer` unten. Ohne sie schickte `dokumentHochladen` NIE eine `rowVersion` mit, und der
   * Ueberschreib-Dialog konnte deshalb strukturell nie erfolgreich sein (jede vorhandene
   * `document_links`-Zeile verlangt sie, siehe `backend/src/documents/repository.ts#legeVersionAn`).
   *
   * `reichweite` (Sammelsendung, Task 4b, MERGE 07.09.2026): die vom Hochladenden getroffene Wahl aus
   * `ReichweiteAuswahl.tsx`, NUR gesetzt, wenn die Bestellung an einer Sendung haengt
   * (`bestellung.dsvShipmentNo`) - siehe `dateienGewaehlt` unten. Steht als SECHSTES Argument, NACH
   * `rowVersion`: die beiden schliessen sich gegenseitig aus (eine Ueberschreibung fragt nie nach der
   * Reichweite, Fixrunde 1 Befund 3), `hochladenAusfuehren` schickt deshalb NIE beide zugleich - bei
   * gesetzter `rowVersion` steht an sechster Stelle ein explizites `undefined`, bei gesetzter `reichweite`
   * (und keiner `rowVersion`) an fuenfter. Die Argumentliste hat seit Task 5 IMMER sieben Plaetze (siehe
   * `fortschritt` gleich unten); vorher war sie bedingt kuerzer, und mehrere Tests vergleichen
   * `onHochladen`s Aufrufe per `toHaveBeenCalledWith` GENAU (Arraylaenge zaehlt mit).
   *
   * ================= Task 5 ("Uploads bis 100 MB"): `fortschritt` UND abgelehnte Zusage ==============
   * `fortschritt` (siebtes Argument): der Rueckruf, ueber den der Upload seinen Anteil (0 bis 1) meldet
   * (`api/dokumente.ts#dokumentHochladen`) und den die Fortschrittszeile dieses Reiters anzeigt. Er geht
   * mit JEDEM Aufruf mit; die Plaetze davor sind `undefined`, soweit `hochladenAusfuehren` sie nicht
   * belegt (`rowVersion` und `reichweite` schliessen sich weiterhin aus). Die Argumentliste ist damit
   * immer siebenstellig - die genauen Aufruf-Vergleiche in den Tests tragen `FORTSCHRITT` am Ende.
   *
   * Die Zusage LEHNT bei einem Fehlschlag mit dem urspruenglichen Fehler AB (frueher: stilles Aufloesen
   * nach einem Toast im Hook). Dieser Reiter faengt ihn je Datei ab und zeigt `uploadFehlerText(…)` als
   * stehende Zeile (`UploadFehlerAnzeige`); die naechste Datei der Auswahl laeuft trotzdem. Gelang der
   * Upload und scheiterte nur das Nachladen der Bestellung, ist es ein `NachladenNachUploadFehler` (Datei
   * gespeichert, siehe `lib/upload.ts`).
   */
  onHochladen: (
    bestellungId: string,
    typeId: string,
    datei: File,
    notiz?: string,
    rowVersion?: number | null,
    reichweite?: Reichweite,
    fortschritt?: (anteil: number) => void,
  ) => void | Promise<void>;
  /**
   * Laedt die GANZE Bestellung neu (nicht nur die Checkliste dieses Reiters) und schreibt sie in den
   * Store - Nachbesserung "Hash-Dedupe" (Runde 4, 07.09.2026, Befund 2): `duplikatBestaetigen` unten
   * ruft dies nach einem erfolgreichen `dokumentVerknuepfen` zusaetzlich zu `checklisteNeuLaden` auf.
   * `App.tsx#dokumentBestellungNachladen` (derselbe Schritt, den `onHochladen` nach einem Upload
   * bereits ausloest) - OHNE dieses Nachladen bleiben `o.pdfs`/der gruene Haken der Checkliste auf
   * ihrem alten Stand: der Verknuepfungs-Erfolgspfad schreibt keine Datei hoch, das eigene SSE-Echo
   * ist unterdrueckt, es gibt also keinen anderen Nachlade-Impuls (siehe Kopfkommentar dort).
   */
  onBestellungNachladen: (bestellungId: string) => Promise<void>;
  /**
   * Die Positionsdokumente in drei Ladezustaenden (Fehleranzeige Paket 2, Task 13, Spec E4), ausschliesslich fuer den separaten
   * Abschnitt aus Task 4. Nur mit `geladen` zeigt er Positionen und Knoepfe - auch die Ablage des Massenuploads; `gescheitert` zeigt
   * die Flaeche mit Grund und „Erneut laden“, `laedt` einen neutralen Hinweis. Ohne Angabe gilt `laedt`: ein Aufrufer, der den Stand
   * nicht reicht, bekommt nie Knoepfe auf unbekanntem Stand.
   */
  positionsDokumenteZustand?: PositionsDokumenteZustand;
  /** Gemeinsamer Nachladeweg des offenen Drawers, fuer erfolgreiche Positionsschreibvorgaenge. */
  positionsDokumenteNeuLaden?: () => Promise<void>;
  /** „Erneut laden“ der Flaeche des gescheiterten Ladens (`App.tsx#positionsDokumenteErneutLaden`); lehnt nie ab, erfuellt erst nach dem Laden. */
  onPositionsDokumenteErneutLaden?: () => Promise<void>;
  /** Positionskennung, die ein Einstieg aus der Artikelzeile bereits ausgewaehlt hat. */
  produktionsbildPosition?: number | null;
  /** Öffnet die gemeinsame Lightbox an einem aktuellen Produktionsbild. */
  onProduktionsbildOeffnen?: ProduktionsbildOeffnen;
  /**
   * `doc_type` aus der Adresse (Spezifikation § 3.9, Aufgabe 7) - hebt die passende Zeile hervor
   * und holt sie in den Sichtbereich. `null`/`undefined`: nichts hervorgehoben. Die Meldung bei
   * einem unbekannten `doc_type` (§ 5.4) lebt NICHT hier, sondern in `App.tsx`: dieser Reiter wird
   * auch ohne Router direkt getestet (`dokumenteTab.test.tsx`) und kennt `useAnsicht` deshalb nicht.
   */
  hervorgehoben?: string | null;
  /** DSV-Belege werden gemeinsam mit dem Speditionsreiter geladen; kein zweiter Abruf im Dokumente-Reiter. */
  belege?: DsvBeleg[] | null;
  /**
   * Laedt die DSV-Belege der offenen Bestellung neu (`App.tsx`, derselbe Ladeweg wie das erste Laden).
   * Gebraucht nach „Kategorie zuweisen …“ (Spec 2026-10-17): der Beleg traegt danach seine
   * `manuelleZuordnung`. Ohne die Angabe bleibt die Belegliste auf ihrem Stand.
   */
  onBelegeNeuLaden?: () => Promise<void>;
  /**
   * Die verknuepfte DSV-Sendung (`App.tsx`, derselbe Wert wie im Reiter Spedition). Gebraucht, damit
   * „Kategorie zuweisen …“ die Zahl der Bestellungen der Sendung aus denselben Quellen rechnet wie dort
   * (`lib/belegKategorien.ts#belegKategorieEinstellung`): der Bestellstand kennt nicht jede Bestellung
   * einer 1:n-Sendung.
   */
  sendung?: SendungsTreffer | null;
  /** Freigabe eines eigenen, nicht positionsbezogenen Dokumentlinks für die aktive Bestellgruppe. */
  onDokumentFreigeben?: (dokument: OrderDocument) => void;
  /**
   * `App.tsx#zeigeHinweis`: der Fehlschlag eines Uploads, fuer den dieser Reiter keine Flaeche mehr hat -
   * er ist abgebaut (Reiterwechsel im Drawer, Drawer zu) oder zeigt inzwischen eine andere Bestellung. Der
   * Text nennt Typ und Bestellnummer vom START des Uploads: `Bestellexcel für BE-10440: <Fehlertext>`
   * (Fix-Runde 1 zu Task 5, Spec 61-63). Gleiches Muster wie `onStehenderHinweis` im Positionen-Grid
   * (`PositionsdokumentAktionen.tsx`). Ohne die Prop geht ein solcher Fehler nur ins Protokoll.
   */
  onStehenderHinweis?: (text: string) => void;
  /**
   * Der Store der Upload-Stellen (`App.tsx#useUploadStellen`, Spec "Upload-Sperren", 2026-10-20): Sperre und
   * Fortschrittszeile je Bestellung liegen dort, nicht im State dieser Instanz - so ueberleben sie Reiter- und
   * Bestellungswechsel. Ohne die Prop legt der Reiter sich einen eigenen Store an. Der Schluessel bleibt
   * `bestellung:<id>`, die Sperre gilt dann also ebenfalls je Bestellung: nach einem Wechsel von A auf B in
   * derselben Instanz ist B frei, bei der Rueckkehr zu A wieder gesperrt. Nur den Abbau der Instanz ueberlebt
   * sie nicht, denn der eigene Store geht mit ihr unter. Die Komponententests, die den Reiter einzeln zeichnen,
   * brauchen die Prop deshalb nicht; ein vergessenes Durchreichen in `App.tsx` faengt nur die App-Verdrahtung.
   */
  uploadStellen?: UploadStellen;
  /**
   * Massenupload (Plan 2026-10-22): `App.tsx` hängt den Dialog ein, dieser Reiter nimmt nur die Ablage entgegen und gibt den
   * Auftrag weiter (`Massenupload/MassenuploadAblage.tsx`). Ohne die Prop gibt es im Abschnitt „Positionsdokumente“ weder
   * Ablage noch Knopf - die Komponententests, die den Reiter einzeln zeichnen, brauchen sie nicht.
   */
  onMassenuploadStarten?: (auftrag: MassenuploadAuftrag) => void;
  /**
   * Wie oft der Massenupload-Dialog geschlossen wurde (`App.tsx#massenuploadGeschlossen`), unverändert an den Abschnitt
   * „Positionsdokumente“ gereicht: er lädt dann seinen Prüfstand nach, denn „Gesehen“ und „Erneut prüfen“ in der Zusammenfassung
   * des Dialogs haben ihn verändert (Review Task 11, W2). Ohne die Prop zählt der Reiter nichts mit.
   */
  massenuploadGeschlossen?: number;
}

export function DokumenteTab({
  bestellung: o,
  bestellungen,
  rechte,
  onAendern,
  onHochladen,
  onBestellungNachladen,
  positionsDokumenteZustand = POSITIONSDOKUMENTE_LAEDT,
  positionsDokumenteNeuLaden,
  onPositionsDokumenteErneutLaden,
  produktionsbildPosition,
  onProduktionsbildOeffnen,
  hervorgehoben,
  belege,
  onBelegeNeuLaden,
  sendung,
  onDokumentFreigeben,
  onStehenderHinweis,
  uploadStellen,
  onMassenuploadStarten,
  massenuploadGeschlossen,
}: DokumenteTabProps) {
  const id = o.id as string;
  const pdfs = (o.pdfs as OrderDocument[] | undefined) || [];
  const orderGroupId = o.orderGroupId as string | null | undefined;
  const orderGroupMemberCount = o.orderGroupMemberCount as number | null | undefined;
  const orderGroupStatus = o.orderGroupStatus as "active" | "dissolved" | null | undefined;
  const hatAktiveGruppe = Boolean(orderGroupId) && orderGroupStatus === "active";
  const darfFuerGruppeFreigeben = (dokument: OrderDocument): boolean =>
    hatAktiveGruppe &&
    typeof dokument.id === "string" &&
    typeof dokument.rowVersion === "number" &&
    !dokument.viaGroupId &&
    !dokument.viaShipmentNo &&
    dokument.jtlPosId === undefined &&
    dokument.positionId === undefined;
  const overwriteHistory = (
    (o.documentOverwriteLog as { id?: string; type?: unknown; note?: unknown; ts?: unknown; actor?: unknown; previous?: { name?: unknown; path?: unknown }[] }[] | undefined) || []
  )
    .slice()
    .reverse();

  const [ausstehend, setAusstehend] = useState<AusstehenderUpload | null>(null);
  // Sammelsendung (Task 4b): siehe Kopfkommentar `WartetAufReichweite` - ein Schritt VOR `ausstehend`.
  const [wartetAufReichweite, setWartetAufReichweite] = useState<WartetAufReichweite | null>(null);
  const [duplikat, setDuplikat] = useState<AusstehenderDuplikat | null>(null);
  /**
   * Task 5 ("Uploads bis 100 MB"): die Meldungen gescheiterter Uploads, EINE ZEILE JE DATEI, stehend bis
   * zum Schliessen (`UploadFehlerAnzeige`). Alle anderen Fehler dieses Reiters (Download, Nachladen, Verknuepfen,
   * Entfernen, Dokumentstatus) gehen in den Fehlerstapel (Fehleranzeige Paket 1) - eine Zeile am Tab-Ende gibt es
   * dafuer nicht mehr. Eine neue Auswahl leert die Liste
   * (`dateienGewaehlt`), nicht der naechste Upload derselben Auswahl: so bleibt der Fehler der zweiten Datei
   * stehen, waehrend die dritte laeuft.
   */
  const [uploadFehler, setUploadFehler] = useState<UploadFehlerListe | null>(null);
  /**
   * Fix-Runde 1: die beiden Ref-Werte, an denen `uploadFehlerMelden` entscheidet, ob es dieser Instanz noch
   * eine Flaeche gibt - `montiertRef` (der Reiter ist abgebaut, sobald der Drawer den Reiter wechselt:
   * `.drawer-body` hat `key={tab:rechte}`) und `aktuelleBestellungRef` (dieselbe Instanz kann auf eine andere
   * Bestellung umschalten). Beide sind Refs statt State, aus dem Grund, den `useMontiertRef` nennt: ein Upload
   * braucht nach Minuten die Werte von HEUTE.
   */
  // `"layout"`, weil `montiertRef` im SELBEN Schritt falsch werden muss wie der Aufraeumschritt der Dialoge unten.
  const montiertRef = useMontiertRef("layout");
  const aktuelleBestellungRef = useRef(id);
  aktuelleBestellungRef.current = id;
  /**
   * Der Besuch der Bestellung (Fehleranzeige Paket 2, Task 12, Ruling R-T12d, Regel L6): ein `AbortController` je Besuch, der
   * Aufraeumschritt des Bestellungswechsels unten bricht ihn ab und legt fuer den naechsten Besuch einen neuen an. „Erneut laden“
   * haengt seinen Lauf an das Signal des Besuchs, in dem es gedrueckt wurde: endet er, NACHDEM die Bestellung gewechselt hat (auch
   * bei A -> B -> A, wo `aktuelleBestellungRef` wieder stimmt), schreibt er weder Daten noch Vermerk. Das Erstladen hat sein eigenes
   * Signal je Effekt (derselbe Zweck).
   */
  const besuch = useRef<AbortController | null>(null);
  if (besuch.current === null) besuch.current = new AbortController();
  // Solange der Reiter steht, verlässt eine daneben abgelegte Datei den Hub nicht (siehe `dateiZiehenNebenDemZielAbfangen`).
  useEffect(() => {
    document.addEventListener("dragover", dateiZiehenNebenDemZielAbfangen);
    document.addEventListener("drop", dateiZiehenNebenDemZielAbfangen);
    return () => {
      document.removeEventListener("dragover", dateiZiehenNebenDemZielAbfangen);
      document.removeEventListener("drop", dateiZiehenNebenDemZielAbfangen);
    };
  }, []);
  /**
   * Solange an DIESER Bestellung eine Auswahl abgearbeitet wird (Pruefen, Hochladen, Nachladen, auch das
   * Warten auf einen Dialog dazwischen): die Datei-Eingaben sind `gesperrt`, und die Fortschrittszeile der
   * laufenden Datei steht als `anzeige` (`null`, solange nichts laeuft). Beides liegt im Store der App
   * (Upload-Sperren, Spec 2026-10-20), Schluessel `bestellung:<id>`: ein Reiterwechsel baut diese Instanz ab,
   * ein Bestellungswechsel zeigt eine andere Bestellung - die Sperre gehoert der Bestellung, nicht der Instanz.
   * Der Store zaehlt (zwei Laeufe duerfen sich ueberlappen, ein Ueberschreib-Dialog waehrend der
   * Nachladephase): gesperrt bleibt, bis der LETZTE fertig ist. `mitUploadSperre` unten beginnt und beendet.
   * Es laeuft immer nur EINE Datei zugleich (die Auswahl wird nacheinander abgearbeitet), deshalb genuegt eine
   * einzelne Anzeige.
   */
  const lokaleStellen = useUploadStellen();
  const stellen = uploadStellen ?? lokaleStellen;
  const { gesperrt, anzeige } = useUploadStelle(stellen, uploadStelle.bestellung(id));
  const dsvShipmentNo = (o.dsvShipmentNo as string | null | undefined) || null;
  const bestellexcelInput = useRef<HTMLInputElement | null>(null);
  const piInput = useRef<HTMLInputElement | null>(null);
  const sonstigesInput = useRef<HTMLInputElement | null>(null);
  /**
   * Nachbesserung (Punkt 4, 07.09.2026): der Auflöser der aktuell offenen `dateiGewaehlt`-Zusage,
   * solange der `DuplikatDialog` auf eine Nutzerentscheidung wartet - `duplikatBestaetigen`/
   * `duplikatAbbrechen` rufen ihn NACH ihrer eigenen Arbeit auf, damit die serialisierte
   * Multi-Upload-Schleife ("Sonstige Dokumente" unten) erst dann mit der naechsten Datei
   * weitermacht. Ein Ref statt State: der Wert selbst loest kein Neuzeichnen aus und muss auch
   * WAEHREND desselben Renders (zwischen `setDuplikat` und dem Warten darauf) schon lesbar sein.
   */
  const duplikatEntscheidungRef = useRef<(() => void) | null>(null);
  /**
   * Wechsel der Bestellung und Abbau (Upload-Sperren, Spec 2026-10-20, Task 5): die drei Upload-Dialoge
   * (`ausstehend`, `wartetAufReichweite`, `duplikat`) gehoeren zu der Bestellung, an der sie entstanden. Sie tragen
   * Datei, Typ und `rowVersion` von A, ihre Bestaetigen-Handler aber die `id` des laufenden Renders: bliebe ein Dialog
   * nach A -> B offen, haengte "Bestaetigen" die Datei von A an B. Der Aufraeumschritt dieses Effekts laeuft bei JEDEM
   * Wechsel von `id` und beim Abbau. Er schliesst die Dialoge und loest eine wartende Duplikat-Entscheidung als
   * "Abbrechen" auf - dann endet `verarbeiteEineDatei`, und `mitUploadSperre` gibt die Sperre an A frei. Ohne das
   * bliebe sie, bis jemand zu A zurueckkehrt und den Dialog beantwortet, nach einem Abbau fuer immer. Nur der
   * DuplikatDialog haelt eine Sperre; Ueberschreib- und Reichweiten-Dialog entstehen VOR `mitUploadSperre`.
   *
   * `useLayoutEffect`: der Router legt Adressaenderungen in `startTransition`, Passiv-Effekte eines solchen Commits
   * liefen erst nach dem Malen - der Dialog von A stuende einen Frame lang unter B.
   *
   * Dasselbe gilt fuer die Ladefehler-Vermerke (Fehleranzeige Paket 2, Task 12, Review W-1): sie gehoeren zum Erstladen der
   * Bestellung, die der Reiter zeigte. Kehrt er nach A -> B zu A zurueck, laedt A NEU; der Vermerk von damals darf dann nicht
   * wieder auftauchen, solange das neue Laden noch laeuft (die Marke `orderId` allein hielte ihn fuer A am Leben). Ebenso endet
   * der Besuch (`besuch`): ein noch laufendes „Erneut laden“ von damals schreibt nichts mehr (R-T12d).
   *
   * Und der offene Entfernen-Dialog (`zuEntfernen`, Re-Review O-2): er traegt das Dokument und die rowVersion von A, sein
   * „Entfernen“ aber die `id` des laufenden Renders - unter B ginge ein DELETE mit dem Link von A an B hinaus.
   */
  useLayoutEffect(
    () => () => {
      setAusstehend(null);
      setWartetAufReichweite(null);
      setDuplikat(null);
      duplikatEntscheidungRef.current?.();
      duplikatEntscheidungRef.current = null;
      setLadefehler(null);
      setZuEntfernen(null);
      besuch.current?.abort();
      besuch.current = new AbortController();
    },
    [id],
  );

  /**
   * Die Dokumenten-Checkliste (`GET /api/orders/:id/document-checklist`) - lokaler State dieses
   * Reiters, NICHT Teil des `Order`-Objekts/Stores (Auftrag "Dokumente manuell abhaken", Umsetzung
   * Punkt 2: "kein globaler Store-Umbau"). `orderId` haelt fest, zu WELCHER Bestellung `eintraege`
   * gehoert - derselbe Zweck wie `bestellungId` in `App.tsx#AnlieferungZustand`: ohne diese Marke
   * zeigte ein Wechsel auf eine andere Bestellung kurzzeitig noch die Checkliste der vorherigen, bis
   * die neue Antwort eintrifft.
   */
  const [checkliste, setCheckliste] = useState<{ orderId: string; eintraege: ChecklistenEintrag[] } | null>(
    null,
  );
  const checklisteEintraege = checkliste && checkliste.orderId === id ? checkliste.eintraege : null;
  const [dokumente, setDokumente] = useState<{ orderId: string; eintraege: DokumentAnsicht[] } | null>(null);
  const dokumentEintraege = dokumente && dokumente.orderId === id ? dokumente.eintraege : null;
  /** Entfernte Dokumente (`GET .../document-removals`) - lokaler State wie die Checkliste, `orderId` aus demselben Grund. */
  const [entfernte, setEntfernte] = useState<{ orderId: string; eintraege: EntferntesDokument[] } | null>(null);
  const entfernteEintraege = entfernte && entfernte.orderId === id ? entfernte.eintraege : null;
  /** Geerbtes Dokument, dessen Entfernen auf die Reichweitenwahl wartet (`EntfernenDialog`). */
  const [zuEntfernen, setZuEntfernen] = useState<OrderDocument | null>(null);
  /**
   * Doppelklick-Schutz: solange ein Entfernen laeuft (DELETE UND anschliessendes Neuladen - bis dahin traegt
   * die Zeile noch die alte `rowVersion`), sind alle Muelleimer gesperrt und `entfernen` sendet kein zweites
   * DELETE. Der Ref ist die synchrone Sperre (zwei Klicks im selben Zeichentakt), der State sperrt die Knoepfe.
   */
  const entfernenLaeuft = useRef(false);
  const [entfernenAktiv, setEntfernenAktiv] = useState(false);
  /**
   * Ladefehler (Fehleranzeige Paket 2, Task 12, Regel L1): welche der drei Teile beim ERSTLADEN dieser Bestellung (und bei
   * „Erneut laden“) gescheitert sind. `null`: keiner. Ein gelungenes Laden desselben Teils - gleich von welchem Weg - nimmt ihn
   * wieder heraus (`ladefehlerAufheben`). Scheitert dagegen ein Nachladen, waehrend schon ein Stand steht (nach einem Upload,
   * einem PATCH ...), ist das L3: der Altstand bleibt, und der schreibende Weg meldet es selbst (R4) - das landet nie hier.
   */
  const [ladefehler, setLadefehler] = useState<LadefehlerStand | null>(null);
  const ladeteile = ladefehler && ladefehler.orderId === id ? ladefehler.teile : null;
  const gescheiterteTeile = LADE_TEILE.filter((teil) => ladeteile?.[teil] !== undefined);
  // Die Dokumentliste fehlt, weil ihr Laden scheiterte: dann gibt es KEINEN Rueckfall auf `o.pdfs` (siehe `dateienFuerTyp`).
  const dokumenteGescheitert = ladeteile?.dokumente !== undefined && dokumentEintraege === null;
  // Dasselbe fuer die Checkliste: ihr Status je Typ („entfaellt“, rowVersion) ist unbekannt, die Zeilen zeigen keinen Zustand.
  const checklisteGescheitert = ladeteile?.checkliste !== undefined && checklisteEintraege === null;

  /** Vermerkt, dass `teil` der Bestellung `bestellungId` nicht geladen werden konnte - nur, solange der Reiter sie zeigt. */
  const ladefehlerMerken = (bestellungId: string, teil: LadeTeil, ursache: unknown): void => {
    // Wie die Ladefunktionen unten: ein Fehlschlag von A, der nach A -> B eintrifft, gehoert nicht in die Ansicht von B.
    if (aktuelleBestellungRef.current !== bestellungId) return;
    setLadefehler((alt) => ({
      orderId: bestellungId,
      teile: { ...(alt !== null && alt.orderId === bestellungId ? alt.teile : {}), [teil]: { ursache } },
    }));
  };

  /** Ein gelungenes Laden von `teil` loescht dessen Vermerk; sind alle weg, verschwindet die Flaeche. */
  const ladefehlerAufheben = (bestellungId: string, teil: LadeTeil): void => {
    setLadefehler((alt) => {
      if (alt === null || alt.orderId !== bestellungId || alt.teile[teil] === undefined) return alt;
      const teile = { ...alt.teile };
      delete teile[teil];
      return Object.keys(teile).length === 0 ? null : { orderId: bestellungId, teile };
    });
  };

  /**
   * Laedt die Checkliste neu und schreibt sie in den lokalen State - Aufrufer: der Ladeeffekt unten
   * (Bestellungswechsel) UND `checklisteStatusUmschalten` nach einem erfolgreichen PATCH (Muster
   * `App.tsx#aufgabenNachladen`: schreiben, DANN serverseitig neu laden statt optimistisch zu
   * mutieren - hier zusaetzlich lokal statt ueber den Bestellungs-Store, weil die Checkliste keine
   * Teil des `Order`-Objekts ist).
   *
   * Upload-Sperren (Task 5): geschrieben wird nur, solange die Instanz DIESE Bestellung zeigt
   * (`aktuelleBestellungRef`). Checkliste und Dokumente sind je EIN Slot; ein Nachladen von A, das nach A -> B
   * eintrifft (etwa am Ende eines Uploads, der an A lief), ersetzte sonst den Stand von B, und B zeigte `null`.
   */
  const checklisteNeuLaden = (bestellungId: string, signal?: AbortSignal): Promise<void> =>
    checklisteLaden(bestellungId, signal)
      .then((antwort) => {
        if (signal?.aborted) return;
        if (aktuelleBestellungRef.current !== bestellungId) return;
        setCheckliste({ orderId: bestellungId, eintraege: antwort.eintraege });
        ladefehlerAufheben(bestellungId, "checkliste");
      })
      .catch((fehlerObjekt: unknown) => {
        if (signal?.aborted) return;
        // Protokoll fuer den Entwickler; die ANZEIGE ist Sache des Aufrufers (siehe unten): `teilLaden` beim Erstladen macht
        // daraus die Flaeche oben im Reiter, die schreibenden Wege melden ein gescheitertes Nachladen als R4-Satz.
        console.error(`Dokumenten-Checkliste der Bestellung "${bestellungId}" konnte nicht geladen werden:`, fehlerObjekt);
        // Nachbesserung G1 (Pruefung b5748fc4): bis hierher endete der Fehler HIER - die zurueckgegebene
        // Zusage loeste sich danach IMMER auf, nie ab. `checklisteStatusUmschalten` unten braucht die
        // Ablehnung aber, um einen Nachlade-Fehlschlag NACH einem erfolgreichen PATCH von einem
        // gescheiterten PATCH selbst zu unterscheiden (siehe dortiger Kommentar) - deshalb hier
        // WEITERGEREICHT statt verschluckt.
        throw fehlerObjekt;
      });

  const dokumenteNeuLaden = (bestellungId: string, signal?: AbortSignal): Promise<void> =>
    dokumenteLaden(bestellungId, signal).then((eintraege) => {
      if (signal?.aborted) return;
      // Wie bei `checklisteNeuLaden`: nur schreiben, solange die Instanz diese Bestellung zeigt.
      if (aktuelleBestellungRef.current !== bestellungId) return;
      setDokumente({ orderId: bestellungId, eintraege });
      ladefehlerAufheben(bestellungId, "dokumente");
    });

  const entfernteNeuLaden = (bestellungId: string, signal?: AbortSignal): Promise<void> =>
    entfernteDokumenteLaden(bestellungId, signal).then((eintraege) => {
      if (signal?.aborted) return;
      // Wie bei `dokumenteNeuLaden`: nur schreiben, solange die Instanz diese Bestellung zeigt.
      if (aktuelleBestellungRef.current !== bestellungId) return;
      setEntfernte({ orderId: bestellungId, eintraege });
      ladefehlerAufheben(bestellungId, "entfernte");
    });

  /**
   * Laedt EINEN der drei Teile als Erstladen bzw. „Erneut laden“ und macht aus einem Fehlschlag den Vermerk, aus dem die
   * Flaeche oben im Reiter entsteht (L1, kein Stapeleintrag: kein Doppel). LOEST NIE AB - die Flaeche ruft sie ueber
   * `erneutLaden`, und eine abgelehnte Zusage fing `<Ladefehler>` nicht ab, sie ginge an den globalen Auffaenger und meldete
   * in den Stapel (Flaeche UND Eintrag). Ein Abbruch (Bestellungs- oder Reiterwechsel, Schliessen: `signal`) schreibt nichts;
   * Abbruch und 401 ohne eigenes `signal` ergeben keinen Text (`fehlertext` -> `null`), die Flaeche zeichnet sich dann nicht.
   */
  const teilLaden = async (bestellungId: string, teil: LadeTeil, signal?: AbortSignal): Promise<void> => {
    // Ruling R-T12f: eine abgeleitete Kennung (`jtl-<n>`, JTL-Bestellung ohne Hub-Zeile) hat in den drei Tabellen keine Zeile,
    // also gibt es nichts zu laden - der Teil gilt OHNE Anfrage als geladen und leer. Fragen ginge schief: die Checkliste
    // antwortet auf eine Nicht-UUID mit 500 (SQLSTATE 22P02, `orderDocuments4a.ts` hat keine UUID-Wache), und das waere bei JEDER
    // solchen Bestellung eine Flaeche mit „Erneut laden“, das nie hilft. Wie `zeitstrahlSendungen.ts` fragt der Reiter solche
    // Kennungen gar nicht erst; kein Vermerk, kein Stapeleintrag, kein Protokoll.
    if (istAbgeleiteteKennung(bestellungId)) {
      leerenStandSetzen(bestellungId, teil, signal);
      return;
    }
    try {
      if (teil === "checkliste") await checklisteNeuLaden(bestellungId, signal);
      else if (teil === "dokumente") await dokumenteNeuLaden(bestellungId, signal);
      else await entfernteNeuLaden(bestellungId, signal);
    } catch (ursache) {
      if (signal?.aborted) return;
      // Ruling R-T12e/R-T12g: SCHUTZWACHE ohne heutigen Ausloeser. Ein 404 ist die Auskunft „keine“, kein Ladefehler: als Vermerk
      // stuende eine Flaeche mit „Erneut laden“, das nie hilft. Heute kommt keiner: die einzige Kennung, auf die eine der Routen
      // mit 404 antwortet (eine Nicht-UUID, und dann nur die beiden Listen; die Checkliste antwortet mit 500, siehe oben), fragt
      // seit R-T12f gar nicht mehr, und fuer eine inzwischen geloeschte Hub-Bestellung antworten alle drei Routen mit 200, weil
      // keine prueft, ob die Bestellung existiert. Der Zweig bleibt, damit ein kuenftiges 404 keine Flaeche erzeugt, die nie
      // hilft. Der Teil gilt als geladen und LEER (in der Form einer leeren Antwort), unter denselben Schreibbedingungen wie beim
      // Erfolg; wie `zustandNachFehlschlag` in `App.tsx` bei den Positionsdokumenten. Nur Erstladen und „Erneut laden“ laufen
      // hierueber - das Nachladen nach einem Schreiben (R4/R4b) ruft die `...NeuLaden` selbst und behaelt sein Verhalten.
      if (ursache instanceof ApiFehler && ursache.status === 404) {
        leerenStandSetzen(bestellungId, teil, signal);
        return;
      }
      ladefehlerMerken(bestellungId, teil, ursache);
    }
  };

  /**
   * Der geladene, LEERE Stand eines Teils (in der Form einer leeren Antwort) - die EINE Stelle fuer beide Wege, die „es gibt
   * nichts“ bedeuten: der 404-Zweig (Schutzwache, R-T12e/R-T12g) und die abgeleitete Kennung ohne Anfrage (R-T12f). Geschrieben wird nur, solange der
   * Besuch lebt (`signal`) und die Instanz diese Bestellung zeigt (`aktuelleBestellungRef`), wie beim Erfolg der `...NeuLaden`.
   */
  const leerenStandSetzen = (bestellungId: string, teil: LadeTeil, signal?: AbortSignal): void => {
    if (signal?.aborted) return;
    if (aktuelleBestellungRef.current !== bestellungId) return;
    if (teil === "checkliste") setCheckliste({ orderId: bestellungId, eintraege: [] });
    else if (teil === "dokumente") setDokumente({ orderId: bestellungId, eintraege: [] });
    else setEntfernte({ orderId: bestellungId, eintraege: [] });
    ladefehlerAufheben(bestellungId, teil);
  };

  /**
   * „Erneut laden“ der Flaeche: laedt NUR die gescheiterten Teile - die Bestellung ist die jetzt gezeigte (die Flaeche traegt
   * `key={id}`, ein Wechsel setzt sie zurueck, und dieser Rueckruf wird je Render frisch gebunden). Die Flaeche bleibt waehrend des
   * Ladens im Baum, deshalb liefert der Rueckruf ein Promise, das erst nach dem Laden erfuellt ist (sonst waere der Knopf nur
   * eine Mikrotask lang gesperrt) und nie ablehnt. Der Lauf haengt am Signal des Besuchs (`besuch`, R-T12d): endet er nach einem
   * Wechsel der Bestellung, schreibt er nichts mehr.
   */
  const erneutLaden = async (): Promise<void> => {
    const signal = besuch.current?.signal;
    await Promise.all(gescheiterteTeile.map((teil) => teilLaden(id, teil, signal)));
  };

  /**
   * Upload-Sperren (Spec 2026-10-20), Task 5: endet an DIESER Bestellung ein Upload, den diese Instanz nicht selbst
   * gestartet hat (die abgebaute Vorgaengerinstanz), kennt sie sein Ergebnis noch nicht: neue Checkliste
   * (`rowVersionFuer` liest sie) und neue Dokumente.
   * Eigene Laeufe laden in `hochladenAusfuehren` selbst nach und melden sich in `mitUploadSperre` mit `alsEigen`
   * an; sie werden deshalb nicht doppelt geladen.
   *
   * Dieser Rueckruf wirft NIE synchron: der Store ruft seine Zuhoerer in einer Kette, und ein Wurf braeche sie ab -
   * die uebrigen Zuhoerer und die Abonnenten (Sperre, Anzeige) bekaemen das Ende nie. Jeder Fehler, auch ein
   * synchroner aus `checklisteLaden`, landet deshalb im `catch` oder im `.catch` des Promise-Zweigs. Die Bestell-ID
   * wird hier ausdruecklich festgehalten: der Fehler gilt dieser Bestellung, nicht der, die beim Eintreffen
   * zufaellig im Render steht, und die Meldung erscheint nur, solange die Instanz noch montiert ist und sie zeigt.
   */
  const alsEigen = useFremdesUploadEnde(stellen, uploadStelle.bestellung(id), () => {
    const bestellungId = id;
    const nachladenGescheitert = (fehlerObjekt: unknown): void => {
      console.error(
        `Bestellung "${bestellungId}" konnte nach dem Ende eines fremden Uploads nicht neu geladen werden:`,
        fehlerObjekt,
      );
      // Die Bedingung `aktuelleBestellungRef.current === bestellungId` bleibt bewusst (Merge-Review e197c863, Minor 1):
      // den fremden Upload hat der Nutzer nicht in dieser Ansicht ausgeloest, der ausloesende Weg meldet sein Ergebnis
      // selbst. Eine Nachlademeldung fuer eine Bestellung, die nicht mehr angezeigt wird, waere Rauschen. Deshalb weicht
      // dieser Rueckruf hier von R-M2 ab, das nur fuer den EIGENEN Upload gilt (`hochladenAusfuehren`).
      if (montiertRef.current && aktuelleBestellungRef.current === bestellungId) {
        // Ob der fremde Upload gelang, weiss diese Instanz nicht (R-T8a, wie in `hochladenAusfuehren`): neutraler Satz
        // statt "Gespeichert ...", die Ursache folgt (R-Z2: ausser sie raet zum Wiederholen).
        meldeNachladeFehler(fehlerObjekt, CHECKLISTE_NACHLADEN_NACH_UPLOAD_MELDUNG);
      }
    };
    try {
      Promise.all([checklisteNeuLaden(bestellungId), dokumenteNeuLaden(bestellungId)]).catch(nachladenGescheitert);
    } catch (fehlerObjekt) {
      nachladenGescheitert(fehlerObjekt);
    }
  });

  // Beim Oeffnen der Bestellung bzw. bei Wechsel von `id` neu laden - Abbruch beim Aufraeumen, damit
  // eine veraltete Antwort (z. B. nach schnellem Bestellungswechsel) nicht mehr in den State
  // geschrieben wird (Muster `App.tsx`, `useEffect` zu `anlieferungZustand`).
  useEffect(() => {
    const abbruch = new AbortController();
    // Die drei Teile laden unabhaengig voneinander; ein Fehlschlag wird je Teil zum Vermerk fuer die Flaeche oben im Reiter
    // (`teilLaden` loest nie ab). Die Leserechte brauchen alle drei nicht (`endpunktMatrix.ts`: Recht `null`), ein 403 ist hier
    // also kein erwarteter Fall, den die Flaeche verschweigen muesste.
    for (const teil of LADE_TEILE) void teilLaden(id, teil, abbruch.signal);
    return () => abbruch.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  /**
   * `analyzePiDoc`/`analyzeOrderExcelDoc`/`analyzeBusinessDocumentDoc` (index.html:12926,12888,12558)
   * - hier ueber die Parser-Naht statt echter PDF.js/SheetJS/Tesseract-Module (siehe `naht.ts`).
   * Ein Fehlschlag beim Laden/Auslesen darf den bereits gemeldeten Upload nicht zuruecknehmen - genau
   * wie im Bestand ("... wurde gespeichert, aber nicht ausgelesen").
   */
  const ladenUndAuslesen = async (typeId: string, datei: File): Promise<void> => {
    try {
      if (typeId === "pi") {
        const parser = await ladeParser();
        const ergebnis = await parser.piAuslesen(datei);
        // BENANNTE LUECKE, laut statt still: `"piImport"` steht in KEINER Feldgruppe
        // (`api/bestellungen.ts#KEIN_FELDGRUPPEN_WEG`) - es ist NICHT_SCHREIBBAR, weil es das
        // Ergebnis der OCR-Auswertung ist und vom Importpfad geschrieben wird, nicht von der SPA.
        // RIEGEL 2 laesst `bestellungAendern` deshalb mit `FeldOhneGruppeFehler` scheitern, BEVOR
        // ueberhaupt etwas gesendet wird: das ausgelesene Ergebnis bleibt ohne dauerhafte Wirkung,
        // aber der Fehlschlag ist LAUT (Konsole + Toast in `App.tsx#drawerAendern`). Das `catch`
        // unten verschluckt ihn NICHT: `bestellungAendern` ist `async`, der Wurf wird also zur
        // abgelehnten Zusage und landet im `.catch` von `drawerAendern`, nicht hier.
        onAendern(id, {
          piImport: {
            sourceName: datei.name,
            extractedAt: new Date().toISOString(),
            fields: { ...ergebnis.felder, items: ergebnis.positionen },
          },
        });
        return;
      }
      if (typeId === "bestellexcel") {
        const parser = await ladeParser();
        const entwurf = await parser.excelEntwurfAuslesen(datei);
        /**
         * SCHARFGESCHALTET (Task 4, Plan 2026-08-17-bestellexcel-anlagemaske) - beendet RULING R15
         * (Pruefung von `a434ea7`, wichtiger Befund 2): bis hierher ging das Ergebnis ABSICHTLICH
         * NICHT an `onAendern`, weil `"orderExcelImport"` NICHT_SCHREIBBAR war - ein Schreibversuch
         * waere direkt NACH einem erfolgreichen Upload mit "Die Bestellung konnte nicht geaendert
         * werden." gescheitert, fuer den Nutzer nicht von einem gescheiterten Upload zu
         * unterscheiden.
         *
         * `order_excel_import` liegt seit diesem Task in der Feldgruppe `extras`
         * (`backend/src/domaene/feldgruppen.ts`) - derselbe Aufruf wie bei `piImport` direkt darueber
         * geht jetzt durch.
         *
         * NACHZUG (F1, Fixwelle nach der Endabnahme): bis hierher stand `parser.excelAuslesen`, die
         * verkuerzte Fassung (nur `items`) - ein Bestand, der ueber die Anlagemaske MIT vollem
         * Entwurf angelegt und danach hier ueberschrieben wurde, verlor `sheetName`/`headers`/
         * `rowCount`/`warnings` still aus der jsonb-Spalte. `excelEntwurfAuslesen` liefert bereits
         * die volle Form (`sourceName`/`sheetName`/`headers`/`rowCount`/`items`/`warnings`/
         * `extractedAt` - `naht.ts#DokumentParser`), wie sie auch die Anlagemaske schreibt
         * (`App.tsx#neueBestellungAnlegen`, Schritt 4b) - keine eigene Zusammensetzung mehr noetig.
         */
        onAendern(id, { orderExcelImport: entwurf });
        return;
      }
      const art = DOCUMENT_OCR_TYPES[typeId]?.kind;
      if (art === "customs" || art === "freight") {
        const parser = await ladeParser();
        await parser.zollAuslesen(datei);
        // Das Ergebnis fliesst ueber `Verteilung.tsx`, keine eigene Ablage hier - siehe Kopfkommentar.
      }
      // `kind === 'ci'` (Sammelrechnung): siehe `naht.ts`, Abschnitt "Zuordnung Dokumenttyp -> Methode".
    } catch { // offen: Paket 3 – PI-/Zoll-Auslesen ist nicht portiert (ParserNichtPortiertFehler), das Ergebnis bleibt leer
      // Siehe Funktionskommentar oben.
    }
  };

  /**
   * Ein gescheiterter Upload (Fix-Runde 1 zu Task 5): Zeile im Reiter, solange es noch einen gibt, der zur Bestellung passt;
   * sonst stehender Hinweis mit Typ und Bestellnummer vom START. Naeheres am Kommentar von `hochladenAusfuehren`.
   */
  const uploadFehlerMelden = (start: UploadStart, ursache: unknown, datei: File): void => {
    const text = uploadFehlerText(ursache, datei);
    if (montiertRef.current && aktuelleBestellungRef.current === start.bestellungId) {
      setUploadFehler((bisher) => ({
        orderId: start.bestellungId,
        meldungen: [...(bisher?.orderId === start.bestellungId ? bisher.meldungen : []), text],
      }));
      return;
    }
    onStehenderHinweis?.(`${start.typBezeichnung} für ${start.bestellNummer}: ${text}`);
  };

  /**
   * Liefert eine `Promise<void>`, die sich AUFLOEST, sobald der Upload UND das Nachladen der Checkliste
   * durch sind - auch nach einem gescheiterten Upload (den faengt diese Funktion selbst ab, siehe unten).
   * `onHochladen` selbst darf weiterhin `void` liefern (bestehende Aufrufer/Tests aendern sich damit
   * nicht), `await` macht daraus in jedem Fall eine abwartbare Zusage fuer die serialisierte
   * Multi-Upload-Schleife unten. `ladenUndAuslesen` bleibt
   * bewusst UNABHAENGIG (Fire-and-Forget wie vorher): fuer "sonstige" (der einzige Aufrufer der
   * Schleife) tut sie ohnehin nichts (siehe deren eigener Kommentar), auf sie zu warten wuerde nur
   * die anderen Upload-Pfade unnoetig verzoegern.
   *
   * `rowVersion` (Fix "Ueberschreiben scheitert immer mit 409", 07.09.2026): nur von
   * `ueberschreibenBestaetigen` gesetzt, unveraendert an `onHochladen` durchgereicht - siehe
   * Kopfkommentar an `DokumenteTabProps.onHochladen`.
   *
   * `reichweite` (Sammelsendung, Task 4b, MERGE 07.09.2026): nur von `verarbeiteEineDatei` gesetzt,
   * NIE zusammen mit `rowVersion` (Fixrunde 1, Befund 3 - eine Ueberschreibung fragt nie nach der
   * Reichweite) - siehe Kopfkommentar an `DokumenteTabProps.onHochladen` fuer die genaue
   * Argumentzahl-Regel.
   *
   * ================= NACHBESSERUNG (Pruefung b33c2006, Befund 1, 07.09.2026): Checkliste-Reload ====
   *
   * Bis zu dieser Nachbesserung lud NUR `onHochladen` (`App.tsx#drawerDokumentHochladen`) die GANZE
   * Bestellung neu (`o.pdfs`) - die rein lokale Checkliste dieses Reiters (`checklisteEintraege`)
   * blieb dabei auf ihrem VORHERIGEN Stand. Da `rowVersionFuer` seit dem Fix "Ueberschreiben
   * scheitert immer mit 409" aber GENAU diese Checkliste als einzige Quelle fuer das Belegt-Gate UND
   * die an `onHochladen` durchgereichte `rowVersion` benutzt, kam der 409, den dieser Fix eigentlich
   * beheben sollte, bei einer ZWEITEN Ueberschreibung DESSELBEN Typs im selben Drawer-Aufenthalt
   * (ohne Reiterwechsel/Neuoeffnen - dann laedt der `useEffect` auf `id` neu) zurueck: die Zeile
   * ueberschreibt sich serverseitig auf `row_version` 2, `checklisteEintraege` zeigte weiter 1, das
   * Backend lehnte den zweiten Versuch ab. Ein zweiter, ebenso betroffener Fall: ein bisher
   * unbelegter Typ bekommt seine erste Datei (Gate schliesst sich korrekt) - eine SOFORT folgende
   * zweite Datei desselben Typs sah `rowVersionFuer` weiterhin `null` (Checkliste veraltet), der
   * `UeberschreibDialog` erschien gar nicht mehr, der Nutzer bekam nur den generischen
   * Fehlschlag-Toast (409, ohne Erklaerung).
   *
   * Muster wie `duplikatBestaetigen` oben (dort: `checklisteNeuLaden` NACH dem eigentlichen
   * Schreibvorgang, eigener `.catch` -> `meldeFehler(…, CHECKLISTE_NACHLADEN_FEHLGESCHLAGEN_MELDUNG)`) -
   * hier reicht ALLEIN `checklisteNeuLaden` (kein zusaetzliches `onBestellungNachladen`/
   * `Promise.all`), weil `onHochladen` das Nachladen der GANZEN Bestellung bereits selbst ausloest.
   * EINGEKETTET statt lose nebenherlaufend: die serialisierte Multi-Upload-Schleife ("Sonstige
   * Dokumente" unten) wartet auf die von hier zurueckgegebene Zusage und darf erst NACH diesem
   * Nachladen mit der naechsten Datei weitermachen - sonst koennte eine zweite Datei desselben,
   * gerade erst belegten Typs noch gegen die alte Checkliste laufen.
   *
   * ================= Task 5 ("Uploads bis 100 MB"): Fehler je Datei, Fortschritt ==================
   * `onHochladen` LEHNT bei einem gescheiterten Upload ab (siehe Kopfkommentar an
   * `DokumenteTabProps.onHochladen`). Diese Funktion faengt das HIER ab und macht daraus die stehende
   * Zeile `uploadFehlerText(fehler, datei)` - ihre eigene Zusage loest sich also weiterhin IMMER auf, damit
   * die naechste Datei der Auswahl laeuft (`dateienVerarbeiten`). Das Nachladen laeuft wie bisher nach
   * JEDEM Versuch, nicht nur nach einem Erfolg: ein Fehlschlag mit veralteter `rowVersion` (409) braucht
   * den frischen Stand fuer den naechsten Versuch ohnehin, ein unnoetiger Ladeversuch nach einem anderen
   * Fehler ist unschaedlich (derselbe, unveraenderte Stand kommt zurueck). Scheitert auch das Nachladen, steht ein
   * Eintrag im Fehlerstapel, der keinen Erfolg behaupten darf (`CHECKLISTE_NACHLADEN_NACH_UPLOAD_MELDUNG`, R-T8a): ob
   * der Upload gelungen ist, sagt dann allein die Zeile des Uploads, nicht dieser Eintrag. Der gescheiterte Upload
   * selbst steht NICHT zusaetzlich im Stapel (keine Doppelmeldung).
   *
   * Waehrend des Sendens steht die Fortschrittszeile (`phase "hochladen"`, `anteil` aus dem
   * `fortschritt`-Rueckruf); `position` (nummer/gesamt) gibt es nur bei einer Auswahl aus mehreren
   * Dateien. Ein Rueckruf, der NACH dem Ende des Uploads noch eintrifft, wird ignoriert - die Zeile soll
   * nicht wieder auftauchen, nachdem sie verschwunden ist. Der Abbruch eines Uploads ist hier kein Thema:
   * es gibt kein `AbortSignal`, nur `ladeHoch` kennt eines.
   *
   * ================= Fix-Runde 1 zu Task 5: wohin der Fehler geht ====================================
   * Ein Upload von 100 MB kann Minuten laufen. Bis er scheitert, ist der Reiter womoeglich abgebaut
   * (Reiterwechsel im Drawer: `.drawer-body` hat `key={tab:rechte}`) oder zeigt eine andere Bestellung.
   * `uploadFehlerMelden` entscheidet deshalb ueber die Refs von HEUTE und den Start von DAMALS: ist die
   * Instanz noch gemountet UND zeigt dieselbe Bestellung wie beim Start, steht der Fehler als Zeile im
   * Reiter; sonst gaebe es keine passende Flaeche, und er geht als stehender Hinweis mit Typ und
   * Bestellnummer an `onStehenderHinweis`. Bestell-ID, Bestellnummer und Typbezeichnung werden dafuer beim
   * START festgehalten (`UploadStart`). Die Fehlerzeilen tragen eine Bestell-ID-Marke und erscheinen nur unter
   * ihrer Bestellung.
   *
   * ================= Upload-Sperren (Spec 2026-10-20): Sperre und Fortschritt im Store der App =====
   * Kehrt man waehrend des laufenden Uploads in den Reiter zurueck (oder wechselt die Bestellung und wieder
   * zurueck), liest die NEUE Instanz Sperre und Fortschrittszeile aus dem Store: sie sind an die Bestellung
   * gebunden (`bestellung:<id>`), nicht an die Instanz, die den Upload gestartet hat. Diese Funktion bekommt
   * dafuer den `lauf` von `mitUploadSperre`: `lauf.zeige(...)` setzt die Zeile, `lauf.zeige(null)` nimmt nur die
   * eigene wieder weg. Die Bestell-ID-Marke an der Anzeige entfaellt, der Schluessel traegt sie. Der Lauf endet
   * erst in `mitUploadSperre`, also NACH dem Nachladen unten - die Sperre geht erst auf, wenn die Checkliste
   * wieder stimmt.
   */
  const hochladenAusfuehren = async (
    lauf: UploadLauf,
    typeId: string,
    datei: File,
    notiz?: string,
    rowVersion?: number | null,
    reichweite?: Reichweite,
    position?: AuswahlPosition,
  ): Promise<void> => {
    void ladenUndAuslesen(typeId, datei);
    const start: UploadStart = {
      bestellungId: id,
      bestellNummer: kennungVon(o, id),
      typBezeichnung: dokumentTypBezeichnung(o, typeId),
    };
    let sendet = true;
    const anzeigen = (anteil: number): void =>
      lauf.zeige({ dateiname: datei.name, phase: "hochladen", anteil, ...position });
    const fortschritt = (anteil: number): void => {
      if (sendet) anzeigen(anteil);
    };
    anzeigen(0);
    try {
      await (rowVersion !== undefined
        ? onHochladen(id, typeId, datei, notiz, rowVersion, undefined, fortschritt)
        : onHochladen(id, typeId, datei, notiz, undefined, reichweite, fortschritt));
    } catch (fehlerObjekt) {
      console.error(`Upload von "${datei.name}" an Bestellung "${start.bestellungId}" ist gescheitert:`, fehlerObjekt);
      uploadFehlerMelden(start, fehlerObjekt, datei);
    } finally {
      sendet = false;
      lauf.zeige(null);
    }
    try {
      await Promise.all([checklisteNeuLaden(id), dokumenteNeuLaden(id)]);
    } catch (fehlerObjekt) {
      console.error(
        `Dokumenten-Checkliste der Bestellung "${id}" konnte nach dem Hochladen nicht neu geladen werden:`,
        fehlerObjekt,
      );
      // Nicht "Gespeichert ...": ob der Upload gelungen ist, sagt seine eigene Zeile, und das Nachladen laeuft nach
      // JEDEM Versuch. Neutraler Satz (R-T8a), die Ursache folgt (R-Z2: ausser sie raet zum Wiederholen).
      //
      // Nur, solange die Instanz montiert ist (Merge-Review e197c863, Minor 2): eine abgebaute Instanz hat keine
      // Anzeige, die veralten koennte. Die neue Instanz nach einem Reiterwechsel laedt ueber das fremde Ende
      // (`useFremdesUploadEnde`) selbst nach und meldet ihr eigenes Scheitern; meldeten beide, stuende ein Eintrag
      // "womoeglich nicht aktuell" im Stapel, obwohl die sichtbare Checkliste gerade frisch geladen ist. Ein
      // Bestellungswechsel in derselben Instanz bleibt unberuehrt: `montiertRef` bleibt dort wahr, die Meldung steht
      // weiter im globalen Stapel (R-M2).
      if (montiertRef.current) meldeNachladeFehler(fehlerObjekt, CHECKLISTE_NACHLADEN_NACH_UPLOAD_MELDUNG);
    }
  };

  /**
   * Sperrt die Datei-Eingaben (`gesperrt`), solange `arbeit` laeuft - auch ueber das Warten auf einen
   * Dialog hinweg. `arbeit` startet SYNCHRON (noch im Klick-Handler des Dialogs), die Sperre ist deshalb
   * kein Grund fuer ein Warten vor dem ersten `onHochladen`-Aufruf.
   *
   * Der Lauf beginnt (zaehlend, `beginne`) an der Bestellung DES STARTS: `id` ist hier die des Renders, in dem
   * der Handler lief, nicht die von spaeter - wechselt die Instanz waehrend des Uploads die Bestellung, bleibt
   * die Sperre bei der Bestellung, an der hochgeladen wird. Er endet im `finally`, auch nach einem Fehler.
   *
   * `alsEigen` meldet den Lauf als den dieser Instanz an (Task 5): sein Ende loest in `useFremdesUploadEnde` kein
   * Nachladen aus, denn `hochladenAusfuehren` laedt selbst nach. Nur das Ende eines Laufs einer ANDEREN Instanz
   * (etwa der abgebauten Vorgaengerin) tut es.
   */
  const mitUploadSperre = async (arbeit: (lauf: UploadLauf) => Promise<void>): Promise<void> => {
    const lauf = alsEigen(stellen.beginne(uploadStelle.bestellung(id)));
    try {
      await arbeit(lauf);
    } finally {
      lauf.beende();
    }
  };

  /**
   * Fix "Ueberschreiben scheitert immer mit 409" (07.09.2026), Nebenbefund "Duplikat-Gate uebersieht
   * Verknuepfungen ohne Datei": ob DIESER Dokumenttyp an dieser Bestellung schon belegt ist, laesst
   * sich NICHT zuverlaessig ueber `pdfs` (nur Eintraege MIT tatsaechlicher Datei) beantworten - eine
   * `document_links`-Zeile OHNE Datei (Status `offen`/`entfaellt`, angelegt ueber
   * `checklisteStatusUmschalten`) belegt den partiellen Unique-Index `ux_document_links_ohne_position`
   * GENAUSO. `ChecklistenEintrag.rowVersion` (`checklisteEintraege`, `GET .../document-checklist`) ist
   * laut Vertrag GENAU DANN `null`, wenn fuer diesen Typ an dieser Bestellung noch KEINE
   * `document_links`-Zeile existiert - unabhaengig davon, ob sie eine Datei traegt (bestaetigt in
   * `backend/src/domaene/checkliste.ts#checklisteFuer`). Das deckt sich exakt mit `legeVersionAn`s
   * eigener Unterscheidung (`erwarteteRowVersion === null` <=> Erstablage) und ist damit das korrekte
   * Signal fuer BEIDE Gates dieses Reiters (hier UND `verarbeiteEineDatei`s `zielBereitsBelegt` unten).
   *
   * Liefert `null`, solange die Checkliste noch nicht geladen ist (`checklisteEintraege === null`) -
   * dieselbe Bedeutung wie eine wirklich unbelegte Erstablage. Bewusst NICHT abgesichert (kein Sperren
   * der Datei-Felder bis zum Laden) - siehe Task-Bericht, Abschnitt "bekannte, bewusst nicht behobene
   * Luecke": ein außergewoehnlich schneller Upload in diesem sehr kurzen Zeitfenster koennte denselben
   * 409 wieder ausloesen, der hier eigentlich behoben wird.
   */
  const rowVersionFuer = (typeId: string): number | null =>
    checklisteEintraege?.find((e) => e.docType === typeId)?.rowVersion ?? null;

  /**
   * `uploadDocFor(type)` (index.html:12685-12706) gefolgt von der Dateiauswahl - hier in EINEM
   * Schritt, fuer einen GANZEN Schwung Dateien statt nur einer (Sammelsendung, Task 4b), MERGE
   * 07.09.2026 additiv ERGAENZT um die globale SHA-256-Duplikatpruefung (Auftrag "Duplikaterkennung
   * beim Dokumenten-Upload per SHA-256", 07.09.2026) - siehe `verarbeiteEineDatei`/`dateienVerarbeiten`
   * unten fuer die Dedupe-Seite, hier bleibt nur das Ueberschreib-Gate und die Reichweiten-Frage.
   *
   * ================= Fixrunde 1, Befund 1 (KRITISCH) - warum ein Schwung, nicht eine Datei ========
   * Bis hierher rief "Sonstige Dokumente" (`multiple`) `dateiGewaehlt` N-mal, EINMAL je Datei; bei
   * verknuepfter Sendung setzte JEDER Aufruf `wartetAufReichweite` auf EIN einzelnes Objekt - der
   * naechste Durchlauf ueberschrieb den vorigen, nur die LETZTE Datei ueberlebte, die uebrigen N-1
   * verschwanden STUMM (kein Toast, kein Fehler). Diese Funktion nimmt jetzt das ganze Bruendel
   * entgegen und stellt GENAU EINE Reichweiten-Frage fuer den gesamten Schwung - die naheliegende
   * Lesart (eine Auswahl, alle Dateien folgen ihr), passend zum eigentlichen Anwendungsfall dieser
   * Aufgabe: Speditionsunterlagen fuer einen Container/eine Sendung auf einen Schlag hochladen.
   *
   * ================= Fixrunde 1, Befund 3 - warum der bestehende-Zweig VOR dem Reichweite-Gate =====
   * Eine weitere Fassung eines bereits bestehenden Dokuments (eigen oder geerbt) behaelt dessen
   * einmal getroffene Reichweite bei - `e.reichweite` hat im Ueberschreib-Zweig von `legeVersionAn`
   * (`backend/src/documents/repository.ts`) keine Wirkung, siehe dortiger Kommentar an
   * `NeueVersionEingabe.reichweite`. Eine Reichweiten-Frage VOR dem Ueberschreiben waere also eine
   * Frage ohne Wirkung - der `bestehende`-Zweig steht deshalb VOR dem `dsvShipmentNo`-Gate, genau
   * der Ablauf, den es schon vor dieser Aufgabe gab (bis auf die neue Mehrfachauswahl). MERGE: das
   * Gate selbst laeuft jetzt ueber `rowVersionFuer` statt ueber `pdfs.filter(...).length` (main-Fix,
   * siehe dessen Kommentar oben) - `bestehende` bleibt weiterhin NUR fuer die Anzeige im
   * `UeberschreibDialog` erhalten.
   *
   * Ohne verknuepfte Sendung gibt es die Reichweiten-Wahl gar nicht (`reichweiteVorgabe` faellt
   * serverseitig ohnehin auf "bestellung" zurueck) - der bisherige Ablauf bleibt fuer die grosse
   * Mehrheit der Bestellungen unveraendert.
   */
  const dateienGewaehlt = (typeId: string, alleDateien: File[]): Promise<void> => {
    // Global Constraint 17 - die UI erspart den serverseitigen Fehlversuch. Grenze (100 MB) und
    // Wortlaut gehoeren `lib/upload.ts`, allen Uploadwegen gemeinsam. Task 5 ("Uploads bis 100 MB"):
    // eine zu grosse Datei bricht NICHT mehr die ganze Auswahl ab - sie bekommt sofort ihre eigene
    // stehende Zeile und wird uebersprungen, die uebrigen laufen weiter. Eine neue Auswahl leert die
    // Upload-Meldungen der vorigen (das `setUploadFehler` ersetzt die Liste).
    setUploadFehler({ orderId: id, meldungen: alleDateien.filter((d) => istZuGross(d)).map((d) => dateiZuGrossText(d)) });
    const dateien = alleDateien.filter((d) => !istZuGross(d));
    const erste = dateien[0];
    if (!erste) return Promise.resolve();
    const bestehende = pdfs.filter((d) => (d.type || "sonstige") === typeId);
    const rowVersion = rowVersionFuer(typeId);
    if (rowVersion !== null && !documentTypeAllowsMultiple(typeId)) {
      // Ueberschreiben bleibt Einzeldatei - dieser Zweig greift nur, wenn `documentTypeAllowsMultiple`
      // falsch ist, und "sonstige" (der einzige Mehrfach-Aufrufer unten) erreicht ihn deshalb nie.
      setAusstehend({ typeId, datei: erste, bestehende, rowVersion });
      return Promise.resolve();
    }
    if (dsvShipmentNo || hatAktiveGruppe) {
      setWartetAufReichweite({ typeId, dateien });
      return Promise.resolve();
    }
    return dateienVerarbeiten(typeId, dateien);
  };

  /** Einzeldatei-Zugang zu `dateienGewaehlt` - fuer jeden Aufrufer ausser "Sonstige Dokumente". */
  const dateiGewaehlt = (typeId: string, datei: File): Promise<void> => dateienGewaehlt(typeId, [datei]);

  /**
   * `ReichweiteDialog#onBestaetigen` - Fortsetzung von `dateienGewaehlt` fuer eine Bestellung an
   * einer Sendung (Sammelsendung, Task 4b). Kein Ueberschreib-Zweig mehr hier (Fixrunde 1, Befund
   * 3): `dateienGewaehlt` hat den `bestehende`-Fall bereits VORHER abgefangen, dieser Wartezustand
   * entsteht also nur noch fuer eine echte Erstablage. DIESELBE Wahl gilt fuer JEDE Datei des
   * Schwungs (Fixrunde 1, Befund 1) - `dateienVerarbeiten` (MERGE) reicht sie an jede einzelne Datei
   * durch, NACH deren eigener Duplikatpruefung.
   */
  const reichweiteBestaetigen = (reichweite: Reichweite): void => {
    if (!wartetAufReichweite) return;
    const { typeId, dateien } = wartetAufReichweite;
    setWartetAufReichweite(null);
    void dateienVerarbeiten(typeId, dateien, reichweite);
  };

  /**
   * MERGE (07.09.2026): serialisiert einen Schwung Dateien - main brauchte das, damit sich zwei
   * Dateien desselben Multi-Uploads beim Hash-Check/Dialog-State (`duplikat`, siehe
   * `verarbeiteEineDatei`) nicht ueberholen. Diese Aufgabe braucht keine eigene Serialisierung,
   * uebernimmt main-Loesung hier aber unveraendert fuer JEDEN Schwung (nicht nur "sonstige" mit
   * Hash-Pruefung) - unschaedlich fuer die uebrigen Aufrufer (immer genau eine Datei) und einheitlich
   * fuer "sonstige" auch OHNE verknuepfte Sendung.
   */
  const dateienVerarbeiten = (typeId: string, dateien: File[], reichweite?: Reichweite): Promise<void> =>
    mitUploadSperre(async (lauf) => {
      for (const [index, datei] of dateien.entries()) {
        // `nummer`/`gesamt` nur bei mehr als einer Datei - sonst zaehlt die Zeile nicht mit.
        const position = dateien.length > 1 ? { nummer: index + 1, gesamt: dateien.length } : undefined;
        await verarbeiteEineDatei(lauf, typeId, datei, reichweite, position);
      }
    });

  /**
   * Der Teil von `dateienGewaehlt`/`reichweiteBestaetigen` NACH dem Ueberschreib-Gate/der
   * Reichweiten-Frage - globale SHA-256-Duplikatpruefung (Auftrag "Duplikaterkennung beim
   * Dokumenten-Upload per SHA-256", 07.09.2026): VOR jedem Upload wird der Hash der Datei berechnet
   * und gegen `GET /api/documents/by-hash/:sha256` geprueft - global ueber ALLE Bestellungen, nicht
   * nur diese hier. Kein Treffer -> Upload wie bisher. Ein Treffer oeffnet `DuplikatDialog` STATT
   * hochzuladen.
   *
   * Liefert eine `Promise<void>`, die sich erst AUFLOEST, wenn diese EINE Datei vollstaendig
   * abgeschlossen ist (Hash berechnet, geprueft, ggf. Dialog gezeigt und auf die Nutzerentscheidung
   * gewartet, dann hochgeladen) - `dateienVerarbeiten` wartet genau darauf, um die naechste Datei
   * desselben Schwungs erst danach zu beginnen (siehe dessen Kommentar zu den zwei main-Fehlern, die
   * das behebt).
   *
   * Fail open (siehe `sha256Hex`): eine Datei, deren Hash sich nicht berechnen laesst, oder eine
   * gescheiterte Pruefanfrage blockiert den Upload nicht - sie faehrt unveraendert mit dem Upload
   * fort, nur protokolliert.
   *
   * AUSNAHME `docType === "sonstige"` (Nachbesserung 07.09.2026): `"sonstige"` ist serverseitig KEIN
   * gueltiger Dokumenttyp (bekannter, dokumentierter Befund B25,
   * `docs/superpowers/reference/2026-08-11-naht-audit.md`) - die Verknuepfungs-Route
   * (`POST .../documents/:docType/link`, siehe `duplikatBestaetigen`) lehnt ihn ab. Ein Hash-Treffer
   * wuerde also einen Dialog zeigen, dessen "Bestaetigen" serverseitig scheitert. B25/B26 selbst
   * werden HIER NICHT geloest (eigenes Vorhaben) - bis dahin laeuft der Upload fuer diesen EINEN Typ
   * unveraendert OHNE Hash-Berechnung/-Pruefung/Dialog.
   *
   * ================= Fix 1, Nachbesserung Runde 4 (07.09.2026): Belegt-Pruefung VOR dem Dialog ====
   *
   * `zielBereitsBelegt` ist fuer JEDEN Aufrufer dieser Funktion strukturell bereits `false`:
   * `dateienGewaehlt` hat den belegten Fall (`rowVersionFuer(typeId) !== null`) schon VORHER
   * abgefangen (siehe dortiger Kommentar) - ein zweiter, unschaedlicher Check bleibt hier trotzdem
   * stehen, an derselben Stelle, die main urspruenglich dafuer vorgesehen hatte (falls diese Funktion
   * je unabhaengig von `dateienGewaehlt` aufgerufen wird).
   */
  const verarbeiteEineDatei = async (
    lauf: UploadLauf,
    typeId: string,
    datei: File,
    reichweite?: Reichweite,
    position?: AuswahlPosition,
  ): Promise<void> => {
    if (typeId === "sonstige") {
      await hochladenAusfuehren(lauf, typeId, datei, undefined, undefined, reichweite, position);
      return;
    }
    // Task 5 ("Uploads bis 100 MB"): Hash UND Abfrage gegen den Server sind die Phase "pruefen" - bei
    // einer grossen Datei dauert schon das Lesen spuerbar. Die Zeile verschwindet, bevor ein Dialog
    // oder der Upload folgt (der Upload setzt seine eigene gleich wieder).
    lauf.zeige({ dateiname: datei.name, phase: "pruefen", anteil: 0, ...position });
    let duplikatTreffer: DokumentPerHashTreffer | null = null;
    try {
      const sha256 = await sha256Hex(datei);
      if (sha256) {
        try {
          const treffer = await dokumentPerHashLaden(sha256);
          // Siehe Kopfkommentar, Abschnitt "Fix 1": ein Treffer zaehlt nur, wenn der Zieltyp an DIESER
          // Bestellung noch NICHT belegt ist - sonst wuerde "Verknuepfen" zwingend am partiellen
          // Unique-Index scheitern. Ueber `rowVersionFuer` statt `pdfs.some(...)`.
          const zielBereitsBelegt = rowVersionFuer(typeId) !== null;
          if (treffer && !zielBereitsBelegt) duplikatTreffer = treffer;
        } catch (fehlerObjekt) { // still: fail open (siehe `sha256Hex`) - eine defekte Duplikatpruefung darf den Upload nicht verhindern
          console.error(`Duplikatpruefung fuer "${datei.name}" konnte nicht durchgefuehrt werden:`, fehlerObjekt);
        }
      }
    } finally {
      lauf.zeige(null);
    }
    if (duplikatTreffer) {
      const treffer = duplikatTreffer;
      // Hash und Anfrage dauern: ist die Instanz inzwischen abgebaut oder zeigt eine andere Bestellung, gibt es keinen
      // Dialog mehr, der zu dieser Datei passt, und niemanden, der ihn beantwortete. Das gilt wie "Abbrechen" und
      // wartet nicht: die Sperre geht mit dem Ende dieser Datei auf (Upload-Sperren, Task 5). `montiertRef` faellt im
      // Layout-Cleanup, zusammen mit dem Aufraeumschritt der Dialoge - zwischen beiden liegt also kein Fenster.
      if (!montiertRef.current || aktuelleBestellungRef.current !== id) return;
      // Wartet auf `duplikatBestaetigen`/`duplikatAbbrechen` (siehe dortiger Aufruf von
      // `duplikatEntscheidungRef.current`) - erst DANN gilt diese Datei als abgeschlossen.
      await new Promise<void>((resolve) => {
        duplikatEntscheidungRef.current = resolve;
        setDuplikat({ typeId, datei, treffer });
      });
      return;
    }
    await hochladenAusfuehren(lauf, typeId, datei, undefined, undefined, reichweite, position);
  };

  /**
   * Bestaetigen im `DuplikatDialog` - verknuepft das VORHANDENE Dokument
   * (`POST .../documents/:docType/link`), laedt NICHTS hoch. Nach Erfolg wird die Checkliste neu
   * geladen (dasselbe Nachlade-Muster wie `checklisteStatusUmschalten` unten) - die neue
   * Verknuepfung ist erst danach in `checklisteEintraege` sichtbar.
   *
   * ================= Fix 2, Nachbesserung Runde 4 (07.09.2026) ====================================
   *
   * `checklisteNeuLaden` allein reichte NICHT: `o.pdfs`/der gruene Haken haengen an der GANZEN
   * Bestellung (`Checkliste.tsx`/`DokumenteTab.tsx#pdfs`), nicht an der rein lokalen Checkliste
   * dieses Reiters - die wurde dabei nirgends neu geladen. Das eigene SSE-Echo ist unterdrueckt, es
   * gibt also auch darueber keinen Nachlade-Impuls: ein Klick auf "Verknuepfen" schloss den Dialog,
   * sichtbar aenderte sich sonst nichts. `onBestellungNachladen` (`App.tsx#dokumentBestellungNachladen`)
   * ist derselbe Nachladeschritt, den der regulaere Upload-Pfad (`onHochladen`) ohnehin schon ausloest -
   * hier zusaetzlich zu `checklisteNeuLaden`, BEIDE parallel (`Promise.all`), damit ein Fehlschlag des
   * einen Zweigs den anderen nicht verzoegert. Ein gescheitertes Nachladen (gleich welcher Zweig) zeigt
   * dieselbe Meldung wie ein gescheitertes Checklisten-Nachladen - die Verknuepfung selbst ist in
   * BEIDEN Faellen bereits gespeichert, nur die Ansicht ist veraltet.
   *
   * `.finally` loest die evtl. wartende `verarbeiteEineDatei`-Zusage auf (Nachbesserung, Punkt 4) -
   * ERST NACHDEM der Verknuepfungsversuch (Erfolg oder Fehlschlag) UND beide Nachladeversuche
   * durchgelaufen sind, damit die serialisierte Multi-Upload-Schleife wirklich erst dann mit der
   * naechsten Datei weitermacht.
   */
  const duplikatBestaetigen = (): void => {
    if (!duplikat) return;
    const { typeId, treffer } = duplikat;
    // Die Entscheidung gehoert ab jetzt DIESEM Bestaetigen: aus dem Ref genommen, wartet sie nicht mehr auf eine
    // Oberflaeche, sondern auf das Netz. Der Aufraeumschritt bei A -> B/Abbau loest so nur Entscheidungen auf, die
    // wirklich noch warten; die Sperre haelt bis nach Verknuepfen und Nachladen, und das `.finally` kann nicht das
    // Warten einer spaeteren Datei (etwa an B) freigeben (Korrekturrunde 1 zu Task 5, G1).
    const entscheidung = duplikatEntscheidungRef.current;
    duplikatEntscheidungRef.current = null;
    setDuplikat(null);
    dokumentVerknuepfen(id, typeId, treffer.documentId)
      .then(() =>
        Promise.all([checklisteNeuLaden(id), dokumenteNeuLaden(id), onBestellungNachladen(id)]).catch((fehlerObjekt: unknown) => {
          console.error(
            `Bestellung "${id}" konnte nach der Verknuepfung nicht vollstaendig neu geladen werden:`,
            fehlerObjekt,
          );
          meldeNachladeFehler(fehlerObjekt, CHECKLISTE_NACHLADEN_FEHLGESCHLAGEN_MELDUNG);
        }),
      )
      .catch((fehlerObjekt: unknown) => {
        console.error(
          `Dokument "${treffer.documentId}" konnte nicht mit Bestellung "${id}" verknuepft werden:`,
          fehlerObjekt,
        );
        meldeFehler(fehlerObjekt, DUPLIKAT_VERKNUEPFUNG_FEHLGESCHLAGEN_MELDUNG);
      })
      .finally(() => {
        entscheidung?.();
      });
  };

  /**
   * Abbrechen im `DuplikatDialog` - verwirft NUR diese eine Datei (Auftrag, Abschnitt "Vom Nutzer
   * bestaetigtes Verhalten"): die uebrigen Dateien einer Multi-Upload-Schleife sind davon unberuehrt
   * (serialisiert seit der Nachbesserung, Punkt 4, warten sie ohnehin erst auf den Abschluss dieser
   * einen Datei - `duplikatEntscheidungRef` gibt genau diese wartende Zusage frei).
   */
  const duplikatAbbrechen = (): void => {
    setDuplikat(null);
    duplikatEntscheidungRef.current?.();
    duplikatEntscheidungRef.current = null;
  };

  /**
   * `confirmDocumentOverwrite()` (index.html:12714-12726). Reicht seit dem Fix "Ueberschreiben
   * scheitert immer mit 409" (07.09.2026) zusaetzlich `ausstehend.rowVersion` an
   * `hochladenAusfuehren` durch - ohne sie wirft `legeVersionAn` fuer diese bereits vorhandene
   * `document_links`-Zeile IMMER einen `VersionConflictError` (409), siehe Kommentar an
   * `AusstehenderUpload.rowVersion`. Keine Reichweite mehr im Gepaeck (Fixrunde 1, Befund 3) - dieser
   * Weg ist immer ein Ueberschreiben, fuer das `reichweite` ohnehin wirkungslos waere.
   */
  const ueberschreibenBestaetigen = (notiz: string): void => {
    if (!ausstehend) return;
    const { typeId, datei, rowVersion } = ausstehend;
    setAusstehend(null);
    void mitUploadSperre((lauf) => hochladenAusfuehren(lauf, typeId, datei, notiz, rowVersion));
  };

  /**
   * `toggleDocManual`/`toggleDocNa` (index.html:12728-12738) - dieselbe Funktion. Fuer JEDEN
   * Checklisten-Typ (Pflicht oder optional) ein Umschalter zwischen `"offen"` und `"entfaellt"` - kein
   * dritter manueller Zustand getrennt von "entfaellt" (Root Cause, siehe unten). Die Beschriftung
   * "manuell abhaken" vs. "Nicht notwendig" bleibt rein kosmetisch; sie folgt seit Spec
   * 2026-11-03-drawer-checkliste-gleichlauf der Server-Sichtbarkeit (`pflicht` aus `zeilenUrteil`, im
   * Rueckfall `t.optional`) - beide rufen hier denselben PATCH mit demselben Wertepaar auf.
   *
   * ================= ROOT CAUSE, behoben (Auftrag "Dokumente manuell abhaken") =================
   *
   * Bis hierher stand hier DIE Schreibstelle fuer `docsManual` - ein Feld, das in KEINER Feldgruppe
   * steht (`api/bestellungen.ts#KEIN_FELDGRUPPEN_WEG`, NICHT_SCHREIBBAR: `order_documents.status`
   * gewinnt, U5). `onAendern(id, { docsManual })` -> `App.tsx#drawerAendern` ->
   * `bestellungAendern` warf deshalb SYNCHRON, VOR jeder Netzwerkanfrage, einen `FeldOhneGruppeFehler`
   * - landete im `.catch` von `drawerAendern`, der pauschal "Die Bestellung konnte nicht geaendert
   * werden." zeigte, OHNE dass je ein Request rausging. Es gab bereits einen fertigen, aber von
   * NIEMANDEM aufgerufenen Endpunkt dafuer: `PATCH /api/orders/:id/documents/:docType`
   * (`backend/src/http/routes/orderDocuments4a.ts`) - dieser Weg jetzt darueber, direkt (lokaler
   * State dieses Reiters, kein Umweg ueber `onAendern`/`App.tsx`).
   */
  const checklisteStatusUmschalten = (typeId: string): void => {
    const eintrag = checklisteEintraege?.find((e) => e.docType === typeId);
    if (!eintrag) {
      // Laut statt still - siehe Root-Cause-Kommentar oben: eine erfundene rowVersion schriebe
      // entweder ins Leere oder ueber einen fremden Stand (Muster `App.tsx#drawerAufgabeAendern`,
      // `versionVon`). Kann nur eintreten, solange die Checkliste noch laedt (kurzes Fenster zwischen
      // Mounten und der ersten Antwort von `GET .../document-checklist`) oder deren Ladeversuch
      // gescheitert ist - `Checkliste.tsx` zeigt den Knopf unabhaengig vom Ladezustand.
      console.error(`Checklisteneintrag "${typeId}" der Bestellung "${id}" ist noch nicht geladen.`);
      // Kein Server im Spiel, also keine Ursache aus einer Antwort: der feste Satz ist die ganze Meldung.
      meldeFehler(DOKUMENTSTATUS_FEHLGESCHLAGEN_MELDUNG);
      return;
    }
    const zielStatus = eintrag.status === "entfaellt" ? "offen" : "entfaellt";
    // Zweiarmiges `.then(onErfolg, onFehler)` statt eines gemeinsamen `.catch` danach (Nachbesserung
    // G1, Pruefung b5748fc4): ein `.catch` NACH `.then(() => checklisteNeuLaden(id))` faengt SOWOHL
    // einen gescheiterten PATCH ALS AUCH ein gescheitertes Nachladen ab, ohne dass sich die beiden
    // Faelle unterscheiden liessen - Root Cause des Befunds war bis hierher aber, dass ein
    // gescheitertes Nachladen dort NIE ankam, weil `checklisteNeuLaden` seinen Fehler damals intern
    // verschluckte. Seit `checklisteNeuLaden` ihn weiterreicht (siehe dessen Kopfkommentar), faengt der
    // eigene `.catch` im Erfolgsarm hier GEZIELT nur das Nachladen ab - der zweite Callback-Arm bleibt
    // fuer den gescheiterten PATCH selbst zustaendig.
    //
    // Spec 2026-10-31-board-dokumentstand: „entfaellt" (und sein Zuruecksetzen) aendert das Lesefeld
    // `dokumentStand` der GANZEN Bestellung - Board-Gruppe „Warten auf Dokumente", Banner und
    // Archivierung lesen nur dieses Feld. Die lokale Checkliste allein reichte nicht: die Bestellung im
    // Store behielte ihren alten Stand, das eigene SSE-Echo ist unterdrueckt. Darum zusaetzlich
    // `onBestellungNachladen`, BEIDE parallel (`Promise.all`, Muster `duplikatBestaetigen`) und mit
    // EINEM gemeinsamen `.catch`: scheitert einer der Zweige oder beide, steht dieselbe Meldung genau
    // einmal da.
    checklisteEintragAendern(id, typeId, zielStatus, eintrag.rowVersion).then(
      () =>
        Promise.all([
          checklisteNeuLaden(id),
          // `checklisteNeuLaden` protokolliert seinen Fehler selbst (siehe dessen Kopfkommentar); dieser Zweig
          // tut es hier, damit jeder Fehlschlag genau EINMAL im Protokoll steht und das gemeinsame `.catch`
          // darunter nur noch meldet.
          (async (): Promise<void> => {
            try {
              await onBestellungNachladen(id);
            } catch (bestellungFehler) {
              console.error(`Bestellung "${id}" konnte nach der Dokumentstatus-Aenderung nicht neu geladen werden:`, bestellungFehler);
              throw bestellungFehler;
            }
          })(),
        ]).catch((nachladeFehler: unknown) => {
          // PATCH war erfolgreich, NUR das Nachladen schlug fehl (protokolliert ist es oben). Ohne diese
          // Meldung blieb die Zeile stumm auf ihrem alten Stand ("offen") stehen; ein zweiter Klick sandte
          // danach eine veraltete `rowVersion` und scheiterte mit 409 (Root-Cause-Kommentar an
          // `CHECKLISTE_NACHLADEN_FEHLGESCHLAGEN_MELDUNG` oben). R4: der Satz sagt, dass gespeichert ist, die
          // Ursache des Nachladens folgt (R-Z2: ausser sie raet zum Wiederholen). Scheitert nur das Nachladen
          // der Bestellung, ist die Checkliste aktuell und veraltet sind Board-Gruppe und Banner - der Satz
          // spricht darum von der Anzeige, nicht von der Checkliste.
          meldeNachladeFehler(nachladeFehler, CHECKLISTE_NACHLADEN_FEHLGESCHLAGEN_MELDUNG);
        }),
      (fehlerObjekt: unknown) => {
        // Wie bei `drawerAufgabeAendern`/`drawerAendern`: der Fehler wird PROTOKOLLIERT, eine eigene,
        // spezifische Meldung (NICHT der generische Bestellungs-Toast, den es in diesem Reiter ohne
        // `App.tsx`-Anbindung ohnehin nicht gibt) macht den Fehlschlag sichtbar.
        console.error(`Dokumentstatus "${typeId}" der Bestellung "${id}" konnte nicht geaendert werden:`, fehlerObjekt);
        meldeFehler(fehlerObjekt, DOKUMENTSTATUS_FEHLGESCHLAGEN_MELDUNG);
      },
    );
  };

  /**
   * Ob der Nutzer Dokumente dieses Typs entfernen darf - dasselbe Recht wie fuer den Upload. Der Knopf
   * erscheint trotzdem immer und wird ohne Recht gesperrt (siehe `ChecklisteProps.darfEntfernen`: der
   * Rechtedurchgang des Drawers erreicht ihn erst nach dem naechsten Zeichnen des Drawers, nicht nach
   * dem Laden der Belegliste). Der Server urteilt ohnehin verbindlich (403).
   */
  const darfEntfernen = (docType: string): boolean => hatRecht(rechte, entfernenRechte(docType));

  /**
   * Nach „Kategorie zuweisen …“ am DSV-Beleg (Spec 2026-10-17): Dokumentliste, Checkliste, ganze
   * Bestellung (`o.pdfs`, gruener Haken) UND Belegliste neu laden - dieselben Nachladewege wie nach einem
   * Upload (`duplikatBestaetigen`), plus die Belegliste, die die `manuelleZuordnung` traegt. Die
   * Zuordnung selbst ist beim Aufruf schon gespeichert, ein Fehlschlag hier heisst nur „Ansicht veraltet“.
   */
  const belegZugeordnet = (): void => {
    Promise.all([
      checklisteNeuLaden(id),
      dokumenteNeuLaden(id),
      onBestellungNachladen(id),
      onBelegeNeuLaden?.(),
    ]).catch((fehlerObjekt: unknown) => {
      console.error(`Bestellung "${id}" konnte nach der Belegzuordnung nicht vollstaendig neu geladen werden:`, fehlerObjekt);
      meldeNachladeFehler(fehlerObjekt, CHECKLISTE_NACHLADEN_FEHLGESCHLAGEN_MELDUNG);
    });
  };
  const belegKategorie = belegKategorieEinstellung(o, bestellungen, sendung, rechte, belegZugeordnet);

  /**
   * Entfernen eines Bestelldokuments (Spec 2026-10-16): `DELETE /api/orders/:id/document-links/:linkId`
   * ueber `api/dokumente.ts#dokumentEntfernen`. Ersetzt `removeDoc(i)` (`index.html:13002-13014`), das
   * `pdfs` ohne die Zeile ueber `onAendern` zurueckschreiben wollte - dieser Weg warf schon VOR dem
   * Senden (`FeldOhneGruppeFehler`: `pdfs` steht in keiner Feldgruppe, `api/bestellungen.ts#
   * KEIN_FELDGRUPPEN_WEG`), und im Backend gab es nie eine Route dafuer.
   *
   * Eigene und behaltene Dokumente fragen nur per `confirm`; ein geerbtes (Sendung, Bestellgruppe,
   * Sammelsendung) oeffnet den `EntfernenDialog` mit der Reichweitenwahl.
   */
  const loeschen = (dokument: OrderDocument): void => {
    if (typeof dokument.id !== "string" || typeof dokument.rowVersion !== "number") {
      console.error(`Dokument "${String(dokument.name)}" der Bestellung "${id}" traegt keine id/rowVersion.`);
      meldeFehler(DOKUMENT_ENTFERNEN_FEHLGESCHLAGEN_MELDUNG);
      return;
    }
    if (entfernenLaeuft.current) return;
    const herkunft = herkunftVon(dokument);
    if (herkunft === "eigen" || herkunft === "behalten") {
      if (!confirm(`„${(dokument.name as string) || ""}“ wirklich entfernen?`)) return;
      void entfernen(dokument, "bestellung");
      return;
    }
    setZuEntfernen(dokument);
  };

  /**
   * Der Schreibweg nach der Rueckfrage. Danach Checkliste, Belegliste, „Entfernte Dokumente“ UND die
   * ganze Bestellung (`o.pdfs`, gruener Haken) neu laden - dieselben Nachladewege wie nach einem Upload
   * (`hochladenAusfuehren`, `duplikatBestaetigen`), kein zweiter. `orderExcelImport: null` kommt ERST
   * danach und nur nach Erfolg: `onAendern` schreibt ueber `App.tsx#drawerAendern` und dispatcht
   * seinerseits, ein Nebenaufruf gehoert hinter den letzten Dispatch der Hauptkette (AGENTS.md,
   * „Store-Wettlauf").
   */
  const entfernen = async (dokument: OrderDocument, reichweite: EntfernReichweite): Promise<void> => {
    if (entfernenLaeuft.current) return;
    entfernenLaeuft.current = true;
    setEntfernenAktiv(true);
    try {
      await entfernenAusfuehren(dokument, reichweite);
    } finally {
      entfernenLaeuft.current = false;
      setEntfernenAktiv(false);
    }
  };

  /**
   * R4b (Fehleranzeige Paket 2, Task 12): nach einem 409 beim Entfernen ist NICHTS entfernt, die Zeile ist veraltet. Erst wird
   * Checkliste und Dokumentliste neu geladen, DANN steht GENAU EIN Eintrag da - nie zuerst einer, der "Der aktuelle Stand ist
   * geladen" behauptet, und danach ein zweiter, der das Gegenteil sagt. Drei Ausgaenge (Vorbilder: `konfliktNachladen` in
   * `useDrawerZahlungen.ts`, `meldeKonfliktNachNachladen` in `useDrawerTasks.ts`):
   * 1. Das Nachladen gelingt: Konfliktsatz + "Der aktuelle Stand ist geladen – bitte prüfen."
   * 2. Es scheitert mit meldbarer Ursache: Konfliktsatz + "Der aktuelle Stand konnte nicht geladen werden – bitte neu laden." und
   *    die Ursache des Nachladens (`meldeFehler`; es wurde nichts geschrieben, ein Wiederholungsrat der Ursache ist harmlos -
   *    kein R4-Satz, deshalb nicht `meldeNachladeFehler`).
   * 3. Es endet ohne meldbaren Text (Abbruch, 401; `fehlertext` ergibt `null`, R-T10c): L6 ("Abbruch und 401 bleiben still") gilt
   *    fuer den LADEfehler. Die gescheiterte ENTFERNUNG meldet R4b immer; nur der Nachsatz zum Stand haengt am Nachladen. Es
   *    steht deshalb trotzdem GENAU EIN Eintrag da, der Konfliktsatz allein, ohne Aussage ueber den Stand.
   *
   * Hat der Reiter inzwischen die Bestellung gewechselt oder ist abgebaut (Ruling R-T12b, wie R-T11e): in die Ansicht wurde nichts
   * geladen (die Ladefunktionen schreiben nur fuer die gezeigte Bestellung), der Nutzer liest den Eintrag aber bei einer anderen.
   * Dann steht wieder der neutrale Satz allein, jetzt mit dem Bezug auf die Bestellung, an der er entstand (`o` und `id` stammen aus
   * dem Render des Klicks).
   */
  const konfliktNachladen = async (): Promise<void> => {
    let nachladeUrsache: unknown;
    let gescheitert = false;
    try {
      await Promise.all([checklisteNeuLaden(id), dokumenteNeuLaden(id)]);
    } catch (fehlerObjekt) {
      console.error(`Bestellung "${id}" konnte nach dem Konflikt beim Entfernen nicht neu geladen werden:`, fehlerObjekt);
      gescheitert = true;
      nachladeUrsache = fehlerObjekt;
    }
    if (!montiertRef.current || aktuelleBestellungRef.current !== id) {
      meldeFehler(mitBestellbezug(DOKUMENT_ENTFERNEN_KONFLIKT_MELDUNG, o, id));
      return;
    }
    if (!gescheitert) {
      meldeFehler(DOKUMENT_ENTFERNEN_KONFLIKT_GELADEN_MELDUNG);
      return;
    }
    // Einmal uebersetzt: ergibt `fehlertext` `null`, steht der neutrale Satz allein; sonst geht der fertige Grund als "Ursache"
    // an den Stapel (er uebersetzt einen String unveraendert, ein zweites `fehlertext` auf dem Fehlerobjekt entfaellt).
    const grund = fehlertext(nachladeUrsache);
    if (grund === null) {
      meldeFehler(DOKUMENT_ENTFERNEN_KONFLIKT_MELDUNG);
      return;
    }
    meldeFehler(grund, DOKUMENT_ENTFERNEN_KONFLIKT_NICHT_GELADEN_MELDUNG);
  };

  const entfernenAusfuehren = async (dokument: OrderDocument, reichweite: EntfernReichweite): Promise<void> => {
    const linkId = dokument.id as string;
    try {
      await dokumentEntfernen(id, linkId, dokument.rowVersion as number, reichweite);
    } catch (fehlerObjekt) {
      console.error(`Dokument "${linkId}" der Bestellung "${id}" konnte nicht entfernt werden:`, fehlerObjekt);
      if (fehlerObjekt instanceof KonfliktFehler) {
        // Die Zeile ist veraltet: neu laden, damit der naechste Versuch die aktuelle rowVersion traegt - und ERST DANACH
        // melden (R4b, `konfliktNachladen`). Der Doppelklick-Schutz haelt bis dahin: eine Zeile mit alter rowVersion
        // gaebe wieder 409.
        await konfliktNachladen();
      } else if (fehlerObjekt instanceof ApiFehler && fehlerObjekt.status === 422) {
        // Der Servertext allein (z. B. „Dokumente der Sammelsendung gelten fuer alle Bestellungen …“) ist
        // die Erklaerung; ein Satz davor waere Wiederholung. `fehlertext()` liest ihn aus dem Antwortkoerper.
        meldeFehler(fehlerObjekt);
      } else {
        meldeFehler(fehlerObjekt, DOKUMENT_ENTFERNEN_FEHLGESCHLAGEN_MELDUNG);
      }
      return;
    }
    // „Entfernte Dokumente“ ist Beiwerk: scheitert nur dieses Laden, bekommt es eine eigene Meldung und
    // der Rest gilt als aktuell (die Checkliste stimmt). Scheitert etwas anderes, gewinnt dessen Meldung.
    // R4: das Entfernen ist gelungen - der Satz sagt es und nennt die Liste, die veraltet ist; die Ursache des
    // Nachladens folgt.
    let nurEntfernteGescheitert = false;
    let entfernteUrsache: unknown;
    try {
      await Promise.all([
        checklisteNeuLaden(id),
        dokumenteNeuLaden(id),
        onBestellungNachladen(id),
        entfernteNeuLaden(id).catch((fehlerObjekt: unknown) => {
          console.error(`Entfernte Dokumente der Bestellung "${id}" konnten nicht geladen werden:`, fehlerObjekt);
          nurEntfernteGescheitert = true;
          entfernteUrsache = fehlerObjekt;
        }),
      ]);
      if (nurEntfernteGescheitert) {
        meldeNachladeFehler(entfernteUrsache, ENTFERNTE_DOKUMENTE_LADEN_FEHLGESCHLAGEN_MELDUNG);
      }
    } catch (fehlerObjekt) {
      console.error(`Bestellung "${id}" konnte nach dem Entfernen nicht vollstaendig neu geladen werden:`, fehlerObjekt);
      meldeNachladeFehler(fehlerObjekt, CHECKLISTE_NACHLADEN_FEHLGESCHLAGEN_MELDUNG);
    }
    if (dokument.type === "bestellexcel") onAendern(id, { orderExcelImport: null });
  };

  /**
   * Root-Cause (rohes `<a href={fileUrl(pfad)}>`, 401-Bug) siehe `api/useDateiDownload.ts` -
   * gemeinsam mit `Checkliste.tsx`/`DsvBelege.tsx`/`PaymentTab/IstZahlung.tsx` dort gebuendelt (bis Ticket H
   * gehoerte `ZahlungTab.tsx` dazu).
   *
   * Fehleranzeige Paket 1, Task 8 (Spec R2): die gemeinsame Fehlerzeile am Tab-Ende (`role="alert"`,
   * geteilt mit Checklisten-Reload, Duplikat-Verknuepfung, Entfernen und Upload-Pruefung) gibt es nicht
   * mehr - jeder dieser Wege meldet an den Fehlerstapel. Der Download deshalb ueber die Stapel-Fassung des
   * Hooks; gleiche Fehler zweier Klicks fasst der Stapel selbst zusammen.
   */
  const { herunterladen } = useDateiDownloadMitStapel(DOKUMENT_DOWNLOAD_FEHLGESCHLAGEN_MELDUNG);

  const dateienFuerTyp = (typ: string): Array<{ d: OrderDocument; i: number }> => dokumentEintraege
    ? dokumentEintraege
      .filter((dokument) => dokument.docType === typ && dokument.fileId !== null)
      .map((dokument) => ({
        d: {
          id: dokument.id,
          type: dokument.docType ?? undefined,
          name: dokument.originalName,
          path: dokument.downloadPath ?? undefined,
          source: dokument.source,
          rowVersion: dokument.rowVersion,
          viaGroupId: dokument.viaGroupId,
          viaShipmentNo: dokument.viaShipmentNo,
          geteiltMit: dokument.geteiltMit ?? undefined,
          groupLabel: dokument.groupLabel,
          herkunft: dokument.herkunft,
          reichweiteAnzahl: dokument.reichweiteAnzahl,
        },
        i: -1,
      }))
    // Scheiterte das Laden der Dokumentliste, gibt es keinen Rueckfall auf `o.pdfs` (Spec, Zeile "Dokumente-Reiter"): die
    // Flaeche oben sagt, dass die Liste fehlt, und eine Datei aus dem Rueckfall liesse sie vollstaendig aussehen. Solange sie
    // noch LAEDT, bleibt der Rueckfall wie bisher.
    : dokumenteGescheitert
      ? []
      : pdfs.map((d, i) => ({ d, i })).filter((eintrag) => (eintrag.d.type || "sonstige") === typ);
  const bestellexcelEintrag = dateienFuerTyp("bestellexcel")[0];
  const piDraft = o.piImport as { fields?: { items?: unknown[] }; sourceName?: unknown } | null | undefined;
  const sonstige = dateienFuerTyp("sonstige");

  let bestellexcelInhalt: ReactNode;
  if (!bestellexcelEintrag) {
    bestellexcelInhalt = (
      <div className="pi-empty">
        {/* Ohne Dokumentliste weiss der Reiter nicht, ob schon ein Bestellexcel da ist: keine Einladung „hochladen“ (Spec E5, Ruling
            R-T12a). Der Knopf selbst und seine Sperre bleiben (E14: Upload-Code unberuehrt, der Server erkennt Duplikate). */}
        {dokumenteGescheitert ? null : <span>Bestellexcel hochladen und für die spätere Artikelübernahme auslesen.</span>}
        <button type="button" className="btn-primary" disabled={gesperrt} onClick={() => bestellexcelInput.current?.click()}>
          <Ikone name="upload" />
          Bestellexcel hochladen
        </button>
      </div>
    );
  } else {
    // Siehe Kopfkommentar, Punkt 2: kein Positionsvorschautisch in diesem Task.
    bestellexcelInhalt = (
      <Dokumentzeile docType={bestellexcelEintrag.d.type} hervorgehoben={hervorgehoben} className="order-excel-card">
        <div className="order-excel-head">
          <div className="doc-ic">
            <Ikone name="file-spreadsheet" />
          </div>
          <div className="order-excel-file">
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                void herunterladen(bestellexcelEintrag.d.path, bestellexcelEintrag.d.name as string);
              }}
            >
              {bestellexcelEintrag.d.name as string}
            </a>
            {bestellexcelEintrag.d.source ? <span className="doc-overwrite-note">
              {bestellexcelEintrag.d.source === "sharepoint_import" ? "SharePoint-Import" : "Hochgeladen"}
            </span> : null}
          </div>
          <div className="order-excel-actions">
            <button type="button" className="chk-btn" disabled={gesperrt} onClick={() => bestellexcelInput.current?.click()}>
              <Ikone name="refresh-cw" />
              Überschreiben
            </button>
            {kannEntfernen(bestellexcelEintrag.d, bestellexcelEintrag.i) ? <button
              type="button"
              className="row-del"
              title={darfEntfernen("bestellexcel") ? "Bestellexcel entfernen" : NUR_LESEN_TITEL}
              disabled={!darfEntfernen("bestellexcel") || entfernenAktiv}
              data-permission={entfernenRechte("bestellexcel").join(" ")}
              onClick={() => loeschen(bestellexcelEintrag.d)}
            >
              <Ikone name="trash-2" />
            </button> : null}
          </div>
        </div>
        {/* Fixrunde 1 zu Task 6, Review-Befund 1: `bestellexcel` ist ein gueltiger docType
            (docTypes.ts), aber NICHT in DOC_TYPES (config/documents.ts) - er erscheint deshalb
            AUSSCHLIESSLICH in dieser Karte, nie in der Checkliste. Ohne diesen Hinweis waere ein
            per "Die ganze Sendung" hochgeladenes Bestellexcel hier ununterscheidbar von einem
            eigenen - der "Ueberschreiben"-Knopf daneben aendert die Datei dann fuer ALLE
            Bestellungen der Sendung, ohne dass es hier sichtbar waere. `.order-excel-card` ist kein
            Flex-Container - ein Geschwister von `.order-excel-head` steht deshalb schon ohne
            weiteres CSS auf einer eigenen Zeile darunter (siehe Kopfkommentar `GeerbtHinweis.tsx`). */}
        <GeerbtHinweis dokument={bestellexcelEintrag.d} />
        {darfFuerGruppeFreigeben(bestellexcelEintrag.d) ? (
          <button type="button" className="btn-ghost" onClick={() => onDokumentFreigeben?.(bestellexcelEintrag.d)}>
            Für Bestellgruppe freigeben
          </button>
        ) : null}
      </Dokumentzeile>
    );
  }

  let piAuswertungInhalt: ReactNode;
  if (!piDraft || !piDraft.fields) {
    piAuswertungInhalt = (
      <div className="pi-empty">
        <span>PI als Text-PDF hochladen und automatisch auslesen.</span>
        <button type="button" className="btn-primary" disabled={gesperrt} onClick={() => piInput.current?.click()}>
          <Ikone name="upload" />
          PI hochladen
        </button>
      </div>
    );
  } else {
    const anzahl = Array.isArray(piDraft.fields.items) ? piDraft.fields.items.length : 0;
    piAuswertungInhalt = (
      <div className="pi-review-ready">
        <div>
          <strong>PI ausgelesen – Prüfung ausstehend</strong>
          <span>
            {(piDraft.sourceName as string) || "PI"} · {NUM.format(anzahl)} Position{anzahl === 1 ? "" : "en"} erkannt
          </span>
        </div>
        {/* `openPiReview` (index.html:12428) - kein `#piReviewOverlay` in diesem Task, siehe Kopfkommentar.
            Tooltip bewusst nutzerverstaendlich formuliert (Nachbesserung: der vorherige Text
            "ist nicht Teil dieses Tasks" war interne Projektsprache, die einem Anwender nichts ueber
            den Grund der Sperre sagt). */}
        <button type="button" className="btn-primary" disabled title="Prüfdialog ist noch nicht verfügbar">
          <Ikone name="scan-search" />
          Jetzt prüfen
        </button>
      </div>
    );
  }

  // Fix-Runde 1: nur, was zur gezeigten Bestellung gehoert. Die Fortschrittszeile (`anzeige`) liefert der Store
  // schon je Bestellung; die Fehlerzeilen tragen eine Bestell-ID-Marke, wie `checkliste`/`dokumente`.
  const sichtbareUploadFehler = uploadFehler && uploadFehler.orderId === id ? uploadFehler.meldungen : [];

  // Der Inhalt des Abschnitts „Positionsdokumente“; die Ablage des Massenuploads umschließt ihn (siehe `onMassenuploadStarten`).
  const positionsdokumenteInhalt = positionsDokumenteNeuLaden ? (
    <Positionsdokumente
      bestellung={o}
      positionsDokumenteZustand={positionsDokumenteZustand}
      positionsDokumenteNeuLaden={positionsDokumenteNeuLaden}
      onPositionsDokumenteErneutLaden={onPositionsDokumenteErneutLaden}
      vorausgewaehltePosition={produktionsbildPosition}
      onProduktionsbildOeffnen={onProduktionsbildOeffnen}
      onStehenderHinweis={onStehenderHinweis}
      rechte={rechte}
      entfernteNeuLaden={() => entfernteNeuLaden(id)}
      uploadStellen={stellen}
      massenuploadGeschlossen={massenuploadGeschlossen}
      bestellungNachladen={() => onBestellungNachladen(id)}
    />
  ) : null;

  return (
    <>
      {/* Die Ansage-Region steht dauerhaft hier, ausserhalb des bedingten Halters (`UploadFortschritt.tsx`, Kopfkommentar). */}
      <UploadAnsage anzeigen={anzeige ? [{ schluessel: uploadStelle.bestellung(id), anzeige }] : []} />
      {/* Task 5 ("Uploads bis 100 MB"), Fix-Runde 1: die Zeile der laufenden Datei und - stehend, bis der Nutzer sie
          schliesst - eine Zeile je gescheiterter oder uebersprungener Datei stehen OBEN, vor den Upload-Stellen
          (Spec: "an der Upload-Stelle"), und bleiben beim Scrollen im Blick (`.upload-meldungen`, sticky im
          scrollenden `.drawer-body`, `neubau/upload.css`). Beide leer: kein Halter im Baum (Baseline `drawer-docs`). */}
      {anzeige || sichtbareUploadFehler.length > 0 ? (
        <div className="upload-meldungen">
          {anzeige ? <UploadFortschritt {...anzeige} /> : null}
          <UploadFehlerAnzeige meldungen={sichtbareUploadFehler} onSchliessen={() => setUploadFehler(null)} />
        </div>
      ) : null}

      {/* Ladefehler (L1, Fehleranzeige Paket 2, Task 12): EINE Flaeche oben fuer alle gescheiterten Erstladungen, vor den Abschnitten.
          `data-permission="view_board"`: lesender Neuversuch - der Rechte-Durchgang des Drawers (`Drawer.tsx`) sperrt sonst jeden Knopf
          ohne `manage_documents`, auch dieses Neuladen (Vorbild `HistoryTab`). `key={id}`: dieselbe Flaeche steht nacheinander fuer
          wechselnde Bestellungen; der Schluessel (Pflicht aus den Reviews) setzt die Sperre ihres Knopfes beim Wechsel sicher zurueck.
          Gemessen: heute traegt schon `ladeteile` (nur Vermerke der aktuellen Bestellung) den Wechsel, die Flaeche verschwindet
          dazwischen - der Schluessel haelt es auch, wenn sich das einmal aendert. */}
      {gescheiterteTeile.length > 0 ? (
        <div data-permission="view_board">
          <Ladefehler
            key={id}
            was={ladefehlerSatz(gescheiterteTeile)}
            ursache={ladeteile?.[gescheiterteTeile[0]!]?.ursache}
            onErneutLaden={erneutLaden}
          />
        </div>
      ) : null}

      <Abschnitt schluessel="docs-order-excel" titel={BESTELLEXCEL_BEZEICHNUNG}>
        {bestellexcelInhalt}
        <input
          type="file"
          aria-label="Bestellexcel"
          accept=".xlsx,.xls,.xlsm,.ods,.csv"
          style={{ display: "none" }}
          disabled={gesperrt}
          ref={bestellexcelInput}
          onChange={(e) => {
            const datei = e.currentTarget.files?.[0];
            e.currentTarget.value = "";
            if (datei) dateiGewaehlt("bestellexcel", datei);
          }}
        />
      </Abschnitt>

      <Abschnitt schluessel="docs-pi-import" titel="PI-Auswertung">
        {piAuswertungInhalt}
        <input
          type="file"
          aria-label="PI-Auswertung hochladen"
          accept=".pdf"
          style={{ display: "none" }}
          disabled={gesperrt}
          ref={piInput}
          onChange={(e) => {
            const datei = e.currentTarget.files?.[0];
            e.currentTarget.value = "";
            if (datei) dateiGewaehlt("pi", datei);
          }}
        />
      </Abschnitt>

      <Abschnitt schluessel="docs-check" titel="Dokumente – Checkliste">
        <Checkliste
          bestellung={o}
          checkliste={checklisteEintraege}
          dokumente={dokumentEintraege}
          dokumenteGescheitert={dokumenteGescheitert}
          checklisteGescheitert={checklisteGescheitert}
          onDateiGewaehlt={dateiGewaehlt}
          onManuellUmschalten={checklisteStatusUmschalten}
          onLoeschen={loeschen}
          darfEntfernen={darfEntfernen}
          entfernenGesperrt={entfernenAktiv}
          uploadGesperrt={gesperrt}
          onDokumentFreigeben={onDokumentFreigeben}
          darfFuerGruppeFreigeben={darfFuerGruppeFreigeben}
          hervorgehoben={hervorgehoben}
          belege={belege}
          belegKategorie={belegKategorie}
          onAuslesen={() => { // offen: Paket 3 – Knopf „Auslesen“ ohne Wirkung (Scheinfunktion, Inventar E)
            // `extractPiFromDoc`/`extractBusinessDocumentFromDoc` (index.html:12660-12661) lesen eine
            // BEREITS hochgeladene Datei erneut vom Server ein. Ohne Upload-Endpunkt (siehe
            // Task-Bericht) steht dafuer kein `File`-Objekt zur Verfuegung - der Knopf steht aus
            // struktureller Treue da, ist hier aber ohne Wirkung. Gemeldet, nicht erfunden.
          }}
        />
      </Abschnitt>

      {belege && belege.length > 0 ? (
        <Abschnitt schluessel="docs-dsv-belege" titel="DSV-Belege (automatisch)">
          <p className="calc-note">
            Diese Belege stammen automatisch aus der DSV-Verknüpfung dieser Bestellung und lassen sich
            nicht bearbeiten. Mit „Kategorie zuweisen …“ lässt sich ein Beleg einer Dokumentkategorie zuordnen.
          </p>
          <DsvBelege bestellungId={id} belege={belege} kategorie={belegKategorie} />
        </Abschnitt>
      ) : null}

      {positionsDokumenteNeuLaden ? (
        <Abschnitt schluessel="docs-positions" titel="Positionsdokumente">
          {/* Die Ablage des Massenuploads („Ordner hochladen“) ist ein Upload-Knopf: bedienbar nur mit geladenem Stand (Task 13, E4). Sie bleibt
              in JEDEM Zustand eingehaengt und bekommt dafuer `bereit`: ein Wechsel des Ladezustands haengt den Abschnitt nicht neu ein
              (Review Task 13, W1, Ruling R-T13c). */}
          {onMassenuploadStarten ? (
            <MassenuploadAblage
              bestellung={o}
              rechte={rechte}
              onStarten={onMassenuploadStarten}
              bereit={positionsDokumenteZustand.art === "geladen"}
            >
              {positionsdokumenteInhalt}
            </MassenuploadAblage>
          ) : (
            positionsdokumenteInhalt
          )}
        </Abschnitt>
      ) : null}

      <Abschnitt schluessel="docs-sonst" titel={SONSTIGE_BEZEICHNUNG}>
        <div className="doc-list">
          {sonstige.length ? (
            sonstige.map((x) => (
              <Dokumentzeile key={typeof x.d.id === "string" ? x.d.id : x.i} docType={x.d.type} hervorgehoben={hervorgehoben} className="doc-row">
                <div className="doc-ic">
                  <Ikone name="file-text" />
                </div>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    void herunterladen(x.d.path, x.d.name as string);
                  }}
                >
                  {x.d.name as string}
                </a>
                {x.d.source ? <span className="doc-overwrite-note">
                  {x.d.source === "sharepoint_import" ? "SharePoint-Import" : "Hochgeladen"}
                </span> : null}
                {kannEntfernen(x.d, x.i) ? <button
                  type="button"
                  className="row-del"
                  title={darfEntfernen("sonstige") ? "Dokument entfernen" : NUR_LESEN_TITEL}
                  disabled={!darfEntfernen("sonstige") || entfernenAktiv}
                  data-permission={entfernenRechte("sonstige").join(" ")}
                  onClick={() => loeschen(x.d)}
                >
                  <Ikone name="trash-2" />
                </button> : null}
                {darfFuerGruppeFreigeben(x.d) ? (
                  <button type="button" className="btn-ghost" onClick={() => onDokumentFreigeben?.(x.d)}>
                    Für Bestellgruppe freigeben
                  </button>
                ) : null}
                <GeerbtHinweis dokument={x.d} />
              </Dokumentzeile>
            ))
          ) : dokumenteGescheitert ? null : (
            // Nicht, wenn die Dokumentliste nicht geladen werden konnte: "Keine ..." behauptete, was die Flaeche oben bestreitet.
            <div style={{ color: "var(--ink-faint)", fontSize: ".85rem" }}>Keine sonstigen Dokumente.</div>
          )}
        </div>
        <button type="button" className="btn-ghost" disabled={gesperrt} onClick={() => sonstigesInput.current?.click()}>
          <Ikone name="upload" />
          Dokument hochladen
        </button>
        <input
          type="file"
          multiple
          aria-label="Sonstiges Dokument hochladen"
          style={{ display: "none" }}
          disabled={gesperrt}
          ref={sonstigesInput}
          onChange={(e) => {
            const dateien = Array.from(e.currentTarget.files || []);
            e.currentTarget.value = "";
            // EIN Aufruf fuer den GANZEN Schwung (Fixrunde 1, Befund 1) - nicht mehr je Datei einzeln,
            // siehe Kopfkommentar `dateienGewaehlt`. Serialisiert `dateienGewaehlt`/`dateienVerarbeiten`
            // selbst (MERGE, Nachbesserung Punkt 4, 07.09.2026) - kein eigener `for`-`await` mehr hier
            // noetig.
            if (dateien.length) void dateienGewaehlt("sonstige", dateien);
          }}
        />
      </Abschnitt>

      {overwriteHistory.length ? (
        <Abschnitt schluessel="docs-overwrite-log" titel={`Überschreibungen · ${NUM.format(overwriteHistory.length)}`}>
          <div className="doc-overwrite-log">
            {overwriteHistory.map((entry) => (
              <div className="doc-row" key={entry.id as string}>
                <div className="doc-ic">
                  <Ikone name="history" />
                </div>
                <div>
                  <strong>
                    {(docTypeForOrder(o, entry.type)?.label as string) ||
                      ehemaligerDokumenttypLabel(entry.type) ||
                      (entry.type as string)}
                  </strong>
                  <span>
                    {`${fmtDT(entry.ts as string)} · ${(entry.actor as string) || "Unbekannt"}`}
                    <br />
                    {entry.note as string}
                  </span>
                  {(entry.previous || []).map((dokument, i) => (
                    <a
                      key={i}
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        void herunterladen(dokument.path, dokument.name as string);
                      }}
                    >
                      Vorherige Datei: {dokument.name as string}
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Abschnitt>
      ) : null}

      {/* Entfernte Dokumente (Spec 2026-10-16): die Datei bleibt abrufbar, mit Zeitpunkt und Nutzer des
          Entfernens. Eingeklappt (`SEC_COLLAPSED`) und nur sichtbar, wenn es Eintraege gibt. */}
      {entfernteEintraege && entfernteEintraege.length ? (
        <Abschnitt schluessel="docs-entfernt" titel={`Entfernte Dokumente · ${NUM.format(entfernteEintraege.length)}`}>
          <div className="doc-removed-list">
            {entfernteEintraege.map((e) => {
              // Ticket F: ein entferntes Artwork/Produktionsbild nennt seine Position (`null` bei Bestelldokumenten).
              const position = entferntesDokumentPosition((o.items || []) as OrderItem[], e.jtlPosId, e.artikelNummer);
              return (
                <div className="doc-row doc-removed-row" key={e.id}>
                  <div className="doc-ic">
                    <Ikone name="file-text" />
                  </div>
                  <div>
                    <a
                      href="#"
                      onClick={(ev) => {
                        ev.preventDefault();
                        void herunterladen(e.downloadPath, e.originalName);
                      }}
                    >
                      {e.originalName}
                    </a>
                    <span className="doc-removed-meta">
                      {`${entferntTypLabel(e.docType)}${position ? ` · ${position}` : ""} – entfernt am ${fmtDT(e.removedAt)} von ${e.removedBy?.name || "unbekannt"}`}
                      {e.reichweite === "alle" ? ` (bei ${entferntReichweiteText(e)})` : ""}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </Abschnitt>
      ) : null}

      <Verteilung bestellung={o} bestellungen={bestellungen} />

      {zuEntfernen ? (
        <EntfernenDialog
          dateiname={(zuEntfernen.name as string) || ""}
          typLabel={(docTypeForOrder(o, zuEntfernen.type)?.label as string) || String(zuEntfernen.type || "Sonstige")}
          herkunft={herkunftVon(zuEntfernen) as "sendung" | "gruppe" | "sammelsendung"}
          anzahl={reichweiteAnzahlVon(zuEntfernen)}
          sendungsNr={(zuEntfernen.viaShipmentNo as string | null | undefined) || dsvShipmentNo}
          onAbbrechen={() => setZuEntfernen(null)}
          onBestaetigen={(reichweite) => {
            const dokument = zuEntfernen;
            setZuEntfernen(null);
            void entfernen(dokument, reichweite);
          }}
        />
      ) : null}

      {ausstehend ? (
        <UeberschreibDialog
          typLabel={(docTypeForOrder(o, ausstehend.typeId)?.label as string) || ausstehend.typeId}
          bestehende={ausstehend.bestehende}
          onAbbrechen={() => setAusstehend(null)}
          onBestaetigen={ueberschreibenBestaetigen}
        />
      ) : null}

      {/* Die Auswahl entsteht bei einer Sendungsverknuepfung oder aktiven Bestellgruppe. Die UI spart
          nur ungueltige Versuche; die Reichweite selbst bleibt serverseitig verbindlich. */}
      {wartetAufReichweite ? (
        <ReichweiteDialog
          typLabel={(docTypeForOrder(o, wartetAufReichweite.typeId)?.label as string) || wartetAufReichweite.typeId}
          docType={wartetAufReichweite.typeId}
          dsvShipmentNo={dsvShipmentNo}
          orderGroupId={orderGroupId}
          orderGroupMemberCount={orderGroupMemberCount}
          orderGroupStatus={orderGroupStatus}
          onAbbrechen={() => setWartetAufReichweite(null)}
          onBestaetigen={reichweiteBestaetigen}
        />
      ) : null}

      {duplikat ? (
        <DuplikatDialog
          typLabel={(docTypeForOrder(o, duplikat.typeId)?.label as string) || duplikat.typeId}
          treffer={duplikat.treffer}
          linkBeschriftung={(link) => {
            const gefunden = bestellungen.find((b) => (b.id as string) === link.orderId);
            // Nachbesserung (Punkt 6, 07.09.2026): Rueckfall auf `link.orderKennung` (`jtl-<n>`,
            // vom Backend mitgeliefert) statt der rohen `link.orderId` (UUID) - der Treffer liegt oft
            // bei einer Bestellung, die hier gerade nicht geladen ist (Archiv, anderer Filter).
            const bestellLabel = gefunden ? kennungVon(gefunden, link.orderId) : link.orderKennung;
            const docLabel = gefunden ? (docTypeForOrder(gefunden, link.docType)?.label as string | undefined) : undefined;
            return docLabel ? `${bestellLabel} · ${docLabel}` : bestellLabel;
          }}
          onAbbrechen={duplikatAbbrechen}
          onBestaetigen={duplikatBestaetigen}
        />
      ) : null}
    </>
  );
}
